import React, {
    ReactNode,
    useEffect,
    useState,
    useMemo,
} from "react";
import {
    Card,
    Grid,
    CardContent,
    Typography,
    Button,
    Box,
    CardHeader,
    List,
    ListItemButton,
    ListItemText,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Divider,
    ListItem,
    TableRow,
    TableCell,
    IconButton,
    Collapse,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    LinearProgress,
    Paper,
    TextField,
    SwipeableDrawer,
    Chip,
    Avatar, // <-- Import Avatar
} from "@mui/material";

import ReactApexChart from 'react-apexcharts';

// import "./style.css"; // Ensure this file exists or remove the import
import axios from "axios";
import HOST_URL from "../../utils/Url"; // Ensure this path is correct
import { secureGetItem } from "../../utils/encryption"; // Make sure to import at the top

import CountUp from 'react-countup';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
// Import New Icons for Stat Cards
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import { getSecureDevId } from "../../utils/storageHelpers";
import { useTranslation } from "react-i18next";

// --- Interfaces ---
interface DocRowProps {
    doc: any;
    isOpen: boolean;
    onToggle: () => void;
    formatDate: (dateString: string | null | undefined) => string;
    theme: any;
    isEvenRow: boolean;
}

// --- Helper Functions ---
const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            // Try parsing common non-standard formats if necessary
            // Example: DD/MM/YYYY or MM-DD-YYYY - handle with a library like moment.js or date-fns for robustness
            console.warn("Could not parse date directly:", dateString);
            return 'Invalid Date';
        }
        const returnDate = date.toLocaleDateString(undefined, { // Use locale default format
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
        return returnDate.replace(/\//g, '-'); // Replace slashes with dashes for consistency
    } catch (e) {
        console.error("Error formatting date:", dateString, e);
        return 'Invalid Date Format';
    }
};


function DocRow({ doc, isOpen, onToggle, formatDate, theme, isEvenRow }: DocRowProps) {
    const necessaryFields = {
        id: doc.docMid,
        fileNo: doc.fileNo || '-',
        definition: doc.fileDef || '-',
        department: doc.department_Name || '-',
        subject: doc.subj || '-',
        date: formatDate(doc.fDate),
        fileCount: doc.member_DocFiles?.length ?? 0,
        pdfPath: doc.member_DocFiles?.[0]?.pdfPath || null // Get first file's PDF path
    };

    // Function to open document preview
    const handlePreview = (e: React.MouseEvent, pdfPath: string, fileName: string) => {
        e.stopPropagation(); // Prevent row click
        if (pdfPath) {
            window.open(pdfPath, '_blank');
        } else {
            alert('Document path not available');
        }
    };

    return (
        <React.Fragment>
            <TableRow
                sx={{
                    '& > *': { borderBottom: 'unset' },
                    backgroundColor: isEvenRow ? theme.palette.action.hover : 'inherit',
                    '&:hover': { backgroundColor: theme.palette.action.selected },
                    cursor: 'pointer',
                }}
                onClick={onToggle}
            >
                <TableCell sx={{ padding: '6px', width: '50px' }}>
                    <IconButton aria-label="expand row" size="small" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
                        {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell sx={{ padding: '6px 10px' }}>{necessaryFields.fileNo}</TableCell>
                <TableCell sx={{ padding: '6px 10px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={necessaryFields.definition}>{necessaryFields.definition}</TableCell>
                <TableCell sx={{ padding: '6px 10px' }}>{necessaryFields.department}</TableCell>
                <TableCell sx={{ padding: '6px 10px' }}>{necessaryFields.date}</TableCell>
                <TableCell sx={{ padding: '6px 10px' }}>{necessaryFields.fileCount}</TableCell>
                <TableCell sx={{ padding: '6px 10px' }} align="center">
                    {necessaryFields.pdfPath ? (
                        <IconButton
                            aria-label="Download document"
                            size="small"
                            onClick={(e) => handlePreview(e, necessaryFields.pdfPath, necessaryFields.fileNo)}
                            sx={{
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.light + '20',
                                }
                            }}
                        >
                            <DownloadIcon  />
                        </IconButton>
                    ) : (
                        <Typography variant="caption" color="textSecondary">-</Typography>
                    )}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, p: 2, backgroundColor: theme.palette.grey[100], borderRadius: '4px', border: `1px solid ${theme.palette.divider}` }}>
                            <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 'medium', color: theme.palette.primary.dark }}>
                                Details for Document #{doc.docMid}
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1, mb: 1 }}>Core Information</Typography>
                            <Grid container spacing={1.5} sx={{ mb: 2, fontSize: '0.9rem' }}>
                                <Grid item xs={12} sm={6} md={4}><Typography variant="body2"><strong>Synopsis:</strong> {doc.synopsis || '-'}</Typography></Grid>
                                <Grid item xs={12} sm={6} md={4}><Typography variant="body2"><strong>Completion:</strong> {doc.complt || '-'}</Typography></Grid>
                                <Grid item xs={12} sm={6} md={4}><Typography variant="body2"><strong>Auth Person:</strong> {doc.authPersn || '-'}</Typography></Grid>
                                <Grid item xs={12} sm={6} md={4}><Typography variant="body2"><strong>Sub-Subject Name:</strong> {doc.sub_Subject_Name || '-'}</Typography></Grid>
                                <Grid item xs={12} sm={6} md={4}><Typography variant="body2"><strong>File Type Name:</strong> {doc.fileType_Name || doc.fileType || '-'}</Typography></Grid>
                                <Grid item xs={12} sm={6} md={4}><Typography variant="body2"><strong>Period From:</strong> {formatDate(doc.perFr)}</Typography></Grid>
                                <Grid item xs={12} sm={6} md={4}><Typography variant="body2"><strong>Period Until:</strong> {formatDate(doc.perUt)}</Typography></Grid>
                                <Grid item xs={12} sm={6} md={4}><Typography variant="body2"><strong>Create Date:</strong> {formatDate(doc.createdate)}</Typography></Grid>
                            </Grid>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
                                Associated Files ({doc.member_DocFiles?.length ?? 0})
                            </Typography>
                            {doc.member_DocFiles && doc.member_DocFiles.length > 0 ? (
                                <Grid container spacing={2} sx={{ pt: 1 }}>
                                    {doc.member_DocFiles.map((file: any, fileIndex: number) => (
                                        <Grid item xs={12} sm={6} md={3} key={file.pdFid || `file-${fileIndex}`}>
                                            <Paper elevation={1} sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                <Typography variant="body1" component="div" sx={{ fontWeight: 'bold', mb: 0.5, wordBreak: 'break-word' }}>
                                                    {file.pdfName || 'Unnamed File'} {file.pdFid ? `(ID: ${file.pdFid})` : ''}
                                                </Typography>
                                                <Divider sx={{ mb: 1 }} />
                                                <Box sx={{ fontSize: '0.8rem', flexGrow: 1 }}>
                                                    <Typography variant="caption" display="block"><strong>Keywords:</strong> {file.keywords || '-'}</Typography>
                                                    <Typography variant="caption" display="block"><strong>Sub Type:</strong> {file.subFtype || '-'}</Typography>
                                                    <Typography variant="caption" display="block"><strong>File Date:</strong> {formatDate(file.pDate)}</Typography>
                                                    <Typography variant="caption" display="block"><strong>Is Main:</strong> {file.isMain ? 'Yes' : 'No'}</Typography>
                                                    <Typography variant="caption" display="block"><strong>Entry Date:</strong> {formatDate(file.entryDate)}</Typography>
                                                    <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all', mt: 0.5 }}>
                                                        <strong>Path:</strong> {file.pdfPath || '-'}
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', mt: 1 }}>No associated files found.</Typography>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}

// Main HomePage Component
export default function HomePage(): ReactNode {
    const theme = useTheme();
    const iOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
    const [filteredDepartmentList, setFilteredDepartmentList] = useState<any[]>([]);
    // --- State ---
    const [TotalCount, setTotalCount] = useState<number>(0);
    const [IndexCount, setIndexCount] = useState<number>(0);
    const [FileCount, setFileCount] = useState<number>(0);
    const [SplitCount, setSplitCount] = useState<number>(0);
    const [DepartmentList, setDepartmentList] = useState<any[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
    const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
    const [deptCountRawData, setDeptCountRawData] = useState<any[]>([]);
    const [deptCountFormattedData, setDeptCountFormattedData] = useState<any[][]>([]);
    const [isDeptChartLoading, setIsDeptChartLoading] = useState<boolean>(false);
    const [deptChartError, setDeptChartError] = useState<string | null>(null);
    const { i18n, t } = useTranslation();
    // Modal (Total Docs) state
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [modalData, setModalData] = useState<any[]>([]);
    const [isLoadingModal, setIsLoadingModal] = useState<boolean>(false);
    const [modalError, setModalError] = useState<string | null>(null);
    const [openRowId, setOpenRowId] = useState<number | null>(null); // For modal rows
    const [searchQuery, setSearchQuery] = useState<string>("");

    // Pagination for Document Details Modal
    const [modalPage, setModalPage] = useState(1);
    const [modalRowsPerPage] = useState(6); // showing 6 rows

    // Modal (Doc Type) state
    const [isDocTypeModalOpen, setIsDocTypeModalOpen] = useState<boolean>(false);
    const [docTypeData, setDocTypeData] = useState<any[]>([]);
    const [isLoadingDocTypeModal, setIsLoadingDocTypeModal] = useState<boolean>(false);
    const [docTypeModalError, setDocTypeModalError] = useState<string | null>(null);
    const [docTypeSearchQuery, setDocTypeSearchQuery] = useState<string>("");

    // Pagination for Document Type Modal
    const [docTypePage, setDocTypePage] = useState(1);
    const [docTypeRowsPerPage] = useState(6); // showing 10 rows

    // Modal (Index/Split Files) state
    const [isIndex, setIndex] = useState<any[]>([]); // Renamed from isIndex to avoid confusion
    const [isTotalFilesModalOpen, setIsTotalFilesModalOpen] = useState<boolean>(false);
    const [isLoadingTotalFiles, setIsLoadingTotalFiles] = useState<boolean>(false);
    const [totalFilesSearchQuery, setTotalFilesSearchQuery] = useState<string>("");

    // Pagination for Total Files Modal
    const [totalFilesPage, setTotalFilesPage] = useState(1);
    const [totalFilesRowsPerPage] = useState(8);

    // Drawer (Department Docs) state
    const [isDeptDrawerOpen, setIsDeptDrawerOpen] = useState<boolean>(false);
    const [selectedDeptData, setSelectedDeptData] = useState<any[]>([]);
    const [isLoadingDeptData, setIsLoadingDeptData] = useState<boolean>(false);
    const [deptDataError, setDeptDataError] = useState<string | null>(null);
    const [deptDataSearchQuery, setDeptDataSearchQuery] = useState<string>("");
    const [openDrawerRowId, setOpenDrawerRowId] = useState<number | null>(null); // For drawer rows

    const [indexSplitData, setIndexSplitData] = useState<any[]>([]);
    const [indexSplitSearch, setIndexSplitSearch] = useState("");
    const [indexSplitPage, setIndexSplitPage] = useState(1);
    const [indexSplitRowsPerPage] = useState(6);
    const [isIndexSplitModalOpen, setIsIndexSplitModalOpen] = useState(false);
    const [isIndexSplitLoading, setIsIndexSplitLoading] = useState(false);
    const [indexSplitType, setIndexSplitType] = useState<"index" | "split" | null>(null);

    // Pagination for Department Drawer
    const [drawerPage, setDrawerPage] = useState(1);
    const [drawerRowsPerPage] = useState(9);

    const [chartOptions, setChartOptions] = useState<any>(null);
    const [chartSeries, setChartSeries] = useState<any[]>([]);

    // --- Effects ---
    useEffect(() => {
        // getTotalDoc();                     // type:1
        getDepartmentTotalDocument();      // type:9
        getDepartmentList();
        getDepartmentCountChartData();
    }, []);



    useEffect(() => {
        if (deptCountRawData && deptCountRawData.length > 0) {
            const categories = deptCountRawData.map(item =>
                item.divisionName || item.department || 'Unknown Dept'
            );
            const seriesData = deptCountRawData.map(item =>
                item.totalCount || item.count || 0
            );

            setChartOptions({
                chart: {
                    type: 'bar',
                    height: '100%',
                    toolbar: {
                        show: true,
                        tools: {
                            download: true,
                            selection: true,
                            zoom: true,
                            zoomin: true,
                            zoomout: true,
                            pan: true,
                            reset: true
                        }
                    },
                    animations: {
                        enabled: true,
                        easing: 'easeinout',
                        speed: 800
                    },
                    zoom: {
                        enabled: true,
                        type: 'x',
                        autoScaleYaxis: true
                    }
                },
                plotOptions: {
                    bar: {
                        borderRadius: 6,
                        horizontal: false,
                        columnWidth: '55%',
                        dataLabels: {
                            position: 'top'
                        }
                    }
                },
                dataLabels: {
                    enabled: true,
                    offsetY: -20,
                    style: {
                        fontSize: '12px',
                        colors: [theme.palette.text.primary]
                    },
                    formatter: function (val: number) {
                        return val.toString();
                    }
                },
                xaxis: {
                    categories: categories,
                    labels: {
                        style: {
                            fontSize: '12px',
                            fontWeight: 600,
                            colors: theme.palette.text.primary
                        },
                        rotate: -45,
                        rotateAlways: true,
                        trim: true,
                        maxHeight: 120
                    },
                    axisBorder: {
                        show: true,
                        color: theme.palette.divider,
                        height: 2
                    },
                    axisTicks: {
                        show: true,
                        color: theme.palette.divider
                    },
                    tooltip: {
                        enabled: false
                    }
                },
                yaxis: {
                    title: {
                        text: t("text.totalDoc"),
                        offsetX: 10,
                        style: {
                            fontSize: '14px',
                            fontWeight: 600,
                            color: theme.palette.text.primary
                        }
                    },
                    labels: {
                        style: {
                            fontSize: '12px',
                            colors: theme.palette.text.secondary
                        },
                        formatter: function (val: number) {
                            return val.toFixed(0);
                        }
                    },
                    forceNiceScale: true,
                    tickAmount: 5
                },
                grid: {
                    borderColor: theme.palette.divider,
                    strokeDashArray: 4,
                    padding: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 5
                    },
                    xaxis: {
                        lines: {
                            show: true
                        }
                    },
                    yaxis: {
                        lines: {
                            show: true
                        }
                    }
                },
                tooltip: {
                    theme: theme.palette.mode,
                    style: {
                        fontSize: '14px'
                    },
                    y: {
                      formatter: function (val: number) {
  return val + ' ' + t("text.documents");
},
                        title: {
                            formatter: function () {
                                return t("text.count");
                            }
                        }
                    }
                },
                colors: [theme.palette.secondary.main],
                fill: {
                    opacity: 0.9,
                    type: 'solid'
                },
                stroke: {
                    show: true,
                    width: 2,
                    colors: ['transparent']
                },
                legend: {
                    show: false
                },
                responsive: [
                    {
                        breakpoint: 1920,
                        options: {
                            chart: {
                                height: '100%'
                            },
                            plotOptions: {
                                bar: {
                                    columnWidth: '60%'
                                }
                            },
                            dataLabels: {
                                style: {
                                    fontSize: '11px'
                                }
                            },
                            xaxis: {
                                labels: {
                                    style: {
                                        fontSize: '11px'
                                    },
                                    rotate: -45
                                }
                            }
                        }
                    },
                    {
                        breakpoint: 1366,
                        options: {
                            chart: {
                                height: '100%'
                            },
                            plotOptions: {
                                bar: {
                                    columnWidth: '65%'
                                }
                            },
                            dataLabels: {
                                style: {
                                    fontSize: '10px'
                                }
                            },
                            xaxis: {
                                labels: {
                                    style: {
                                        fontSize: '10px'
                                    },
                                    rotate: -45
                                }
                            }
                        }
                    },
                    {
                        breakpoint: 1024,
                        options: {
                            chart: {
                                height: '100%'
                            },
                            plotOptions: {
                                bar: {
                                    columnWidth: '70%'
                                }
                            },
                            dataLabels: {
                                enabled: true,
                                style: {
                                    fontSize: '9px'
                                }
                            },
                            xaxis: {
                                labels: {
                                    style: {
                                        fontSize: '9px'
                                    },
                                    rotate: -45
                                }
                            }
                        }
                    },
                    {
                        breakpoint: 768,
                        options: {
                            chart: {
                                height: '100%'
                            },
                            plotOptions: {
                                bar: {
                                    columnWidth: '75%',
                                    borderRadius: 4
                                }
                            },
                            dataLabels: {
                                enabled: true,
                                style: {
                                    fontSize: '8px'
                                },
                                offsetY: -15
                            },
                            xaxis: {
                                labels: {
                                    style: {
                                        fontSize: '8px'
                                    },
                                    rotate: -45,
                                    maxHeight: 80
                                }
                            },
                            yaxis: {
                                labels: {
                                    style: {
                                        fontSize: '9px'
                                    }
                                },
                                title: {
                                    style: {
                                        fontSize: '10px'
                                    }
                                }
                            }
                        }
                    },
                    {
                        breakpoint: 480,
                        options: {
                            chart: {
                                height: '100%'
                            },
                            plotOptions: {
                                bar: {
                                    columnWidth: '80%',
                                    borderRadius: 3
                                }
                            },
                            dataLabels: {
                                enabled: false // Hide data labels on very small screens
                            },
                            xaxis: {
                                labels: {
                                    style: {
                                        fontSize: '7px'
                                    },
                                    rotate: -45,
                                    maxHeight: 60
                                }
                            },
                            yaxis: {
                                labels: {
                                    style: {
                                        fontSize: '8px'
                                    }
                                },
                                title: {
                                    style: {
                                        fontSize: '9px'
                                    }
                                }
                            },
                            grid: {
                                padding: {
                                    left: 5,
                                    right: 5
                                }
                            }
                        }
                    }
                ]
            });

            setChartSeries([{
                name: 'Document Count',
                data: seriesData
            }]);
        } else {
            setChartOptions(null);
            setChartSeries([]);
        }
    }, [deptCountRawData, theme, i18n.language]);
    useEffect(() => {
        if (DepartmentList.length > 0) {
            getTotalDoc();
        }
    }, [DepartmentList]);

    // const getTotalDoc = async () => {
    //     try {
    //         const devDepartments = DepartmentList; // NewNodeMaster se aaya hua

    //         // 🔥 ADMIN CONDITION
    //         if (devDepartments.length > 1) {

    //             const collectData = {
    //                 type: 1,
    //                 pageID: 1,
    //                 searchBy: "",
    //                 fromdt: "1900-01-01",
    //                 todt: new Date().toISOString().slice(0, 10),
    //                 print: 1
    //             };

    //             const res = await axios.post(
    //                 HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`,
    //                 collectData
    //             );

    //             const stats = res?.data?.data?.dashboardcls?.[0];

    //             if (stats) {
    //                 setTotalCount(stats.totDoc || 0);   // ✅ Admin ko full total
    //                 setFileCount(stats.totType || 0);
    //                 setIndexCount(stats.totIndexFile || 0);
    //                 setSplitCount(stats.totsplitedFile || 0);
    //             }

    //         } else {

    //             // 🔥 NORMAL USER CONDITION
    //             const collectData = {
    //                 type: 9,
    //                 pageID: 1,
    //                 searchBy: "",
    //                 fromdt: "1900-01-01",
    //                 todt: new Date().toISOString().slice(0, 10),
    //                 print: 1
    //             };

    //             const res = await axios.post(
    //                 HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`,
    //                 collectData
    //             );

    //             const deptList = res?.data?.data?.departmentwiseCountRest;

    //             if (Array.isArray(deptList) && devDepartments.length === 1) {

    //                 const deptId = devDepartments[0].id;

    //                 const matchedDept = deptList.find(
    //                     (item: any) => item.divisionId === deptId
    //                 );

    //                 setTotalCount(matchedDept?.totalCount || 0);
    //             }

    //             // 👇 Baaki counts type:1 se hi lo
    //             const res2 = await axios.post(
    //                 HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`,
    //                 {
    //                     type: 1,
    //                     pageID: 1,
    //                     searchBy: "",
    //                     fromdt: "1900-01-01",
    //                     todt: new Date().toISOString().slice(0, 10),
    //                     print: 1
    //                 }
    //             );

    //             const stats = res2?.data?.data?.dashboardcls?.[0];

    //             if (stats) {
    //                 setFileCount(stats.totType || 0);
    //                 setIndexCount(stats.totIndexFile || 0);
    //                 setSplitCount(stats.totsplitedFile || 0);
    //             }
    //         }

    //     } catch (error) {
    //         console.error(error);
    //     }
    // };

    const getIndexOrSplitData = async (type: number) => {

        try {

            // ⭐ RESET STATES
            setIndexSplitPage(1);
            setIndexSplitSearch("");
            setIndexSplitData([]);

            setIsIndexSplitModalOpen(true);
            setIsIndexSplitLoading(true);

            const res = await axios.post(
                HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`,
                {
                    type: type,
                    pageID: 1,
                    searchBy: "",
                    fromdt: "1900-01-01",
                    todt: new Date().toISOString().slice(0, 10),
                    print: 1
                }
            );

            let data: any[] = [];

            if (type === 11) {

                setIndexSplitType("index");

                data = res?.data?.data?.departmentwiseIndexinglist || [];

            }

            if (type === 12) {

                setIndexSplitType("split");

                data = res?.data?.data?.departmentwiseIndexingsPendinglist || [];

            }

            const devDepartments = DepartmentList;
            const devId = Number(getSecureDevId());

            // Department user filter

            if (devDepartments.length === 1) {

                data = data.filter((item: any) => item.deptID === devId);

            }

            setIndexSplitData(data);

        } catch (error) {

            console.error(error);

        } finally {

            setIsIndexSplitLoading(false);

        }
    };

    const handleCloseIndexSplitModal = () => {

        setIsIndexSplitModalOpen(false);

        setIndexSplitPage(1);
        setIndexSplitSearch("");
        setIndexSplitData([]);

    };
    const filteredIndexSplitData = useMemo(() => {

        if (!indexSplitSearch) return indexSplitData;

        const q = indexSplitSearch.toLowerCase();

        return indexSplitData.filter((item: any) =>
            item.fileNo?.toLowerCase().includes(q) ||
            item.fileDef?.toLowerCase().includes(q) ||
            item.departmentName?.toLowerCase().includes(q)
        );

    }, [indexSplitData, indexSplitSearch]);
    const getTotalDoc = async () => {
        try {

            const devDepartments = DepartmentList;

            // ================= ADMIN =================
            if (devDepartments.length > 1) {

                const res = await axios.post(
                    HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`,
                    {
                        type: 1,
                        pageID: 1,
                        searchBy: "",
                        fromdt: "1900-01-01",
                        todt: new Date().toISOString().slice(0, 10),
                        print: 1
                    }
                );

                const stats = res?.data?.data?.dashboardcls?.[0];

                if (stats) {
                    setTotalCount(stats.totDoc || 0);
                    setFileCount(stats.totType || 0);

                    // ✅ Admin Index
                    setIndexCount(stats.totIndexFile || 0);

                    // ✅ Admin Split
                    setSplitCount(stats.pending || 0);
                }

            }

            // ================= DEPARTMENT USER =================
            else {

                const res = await axios.post(
                    HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`,
                    {
                        type: 9,
                        pageID: 1,
                        searchBy: "",
                        fromdt: "1900-01-01",
                        todt: new Date().toISOString().slice(0, 10),
                        print: 1
                    }
                );

                const deptList = res?.data?.data?.departmentwiseCountRest;

                if (Array.isArray(deptList)) {

                    const devId = Number(getSecureDevId());

                    const matchedDept = deptList.find(
                        (item: any) => item.divisionId === devId
                    );

                    if (matchedDept) {

                        // ✅ Total Document
                        setTotalCount(matchedDept.totalCount || 0);

                        // ✅ Index File
                        setIndexCount(matchedDept.totalIndexed || 0);

                        // ✅ Split File
                        setSplitCount(matchedDept.remainingIndex || 0);
                    }

                }

                // ===== File Type =====

                const res2 = await axios.post(
                    HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`,
                    {
                        type: 1,
                        pageID: 1,
                        searchBy: "",
                        fromdt: "1900-01-01",
                        todt: new Date().toISOString().slice(0, 10),
                        print: 1
                    }
                );

                const stats = res2?.data?.data?.dashboardcls?.[0];

                if (stats) {
                    setFileCount(stats.totType || 0);
                }

            }

        } catch (error) {
            console.error(error);
        }
    };

    const getDepartmentTotalDocument = async () => {
        try {
            const collectData = {
                type: 9,
                pageID: 1,
                searchBy: "",
                fromdt: "1900-01-01",
                todt: new Date().toISOString().slice(0, 10),
                print: 1
            };

            const res = await axios.post(
                HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`,
                collectData
            );

            const deptList = res?.data?.data?.departmentwiseCountRest;

            if (Array.isArray(deptList)) {

                const devId = Number(getSecureDevId());

                const matchedDept = deptList.find(
                    (item: any) => item.divisionId === devId
                );

                if (matchedDept) {
                    setTotalCount(matchedDept.totalCount || 0);
                } else {
                    setTotalCount(0);
                }
            }

        } catch (error) {
            console.error(error);
            setTotalCount(0);
        }
    };

    const getTotalFilesDataAndShowModal = () => {
        setIsLoadingTotalFiles(true);
        setIndex([]); // Reset state
        setTotalFilesSearchQuery("");
        setTotalFilesPage(1); // Reset to first page
        setIsTotalFilesModalOpen(true);
        // Adjusted type based on likely API expectation (check your API docs)
        const collectData = { type: 10, pageID: 1, searchBy: "", fromdt: "1900-01-01", todt: new Date().toISOString().slice(0, 10), print: 1 };
        axios.post(HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`, collectData)
            .then((res) => {
                const data = res?.data?.data?.totalIndex; // Check path based on actual API response
                setIndex(Array.isArray(data) ? data : []);
            })
            .catch(error => {
                console.error("Error fetching total index/split files:", error);
                setIndex([]); // Ensure state is array on error
            })
            .finally(() => setIsLoadingTotalFiles(false));
    };


    const getDepartmentList = () => {
        try {
            // Use secureGetItem instead of localStorage.getItem
            const devIdData = secureGetItem("devID");
            const devId = devIdData !== null && devIdData !== "null" ? devIdData : -1;

            const collectData = {
                id: typeof devId === 'number' ? devId : -1,
                nodeID: -1,
                titleID: -1
            };

            axios.post(HOST_URL.HOST_URL2 + `NewNodeMaster/GetNewNodeMaster`, collectData)
                .then((res) => {
                    const departments: any[] = res?.data?.data || [];
                    setDepartmentList(departments);
                    setFilteredDepartmentList(departments); // Initialize filtered list
                })
                .catch(error => console.error("Error fetching department list:", error));
        } catch (error) {
            console.error("Error getting devID from secure storage:", error);

            // Fallback: try regular localStorage
            try {
                const devId = localStorage.getItem("devID");
                const collectData = {
                    id: devId === null || devId === "null" ? -1 : Number(devId),
                    nodeID: -1,
                    titleID: -1
                };

                axios.post(HOST_URL.HOST_URL2 + `NewNodeMaster/GetNewNodeMaster`, collectData)
                    .then((res) => {
                        const departments: any[] = res?.data?.data || [];
                        setDepartmentList(departments);
                        setFilteredDepartmentList(departments);
                    })
                    .catch(fallbackError => console.error("Error in fallback department fetch:", fallbackError));
            } catch (fallbackError) {
                console.error("Fallback also failed:", fallbackError);
                setDepartmentList([]);
                setFilteredDepartmentList([]);
            }
        }
    };

    const getDepartmentCountChartData = () => {
        setIsDeptChartLoading(true);
        setDeptChartError(null);
        // Adjusted type based on likely API expectation (check your API docs)
        const collectData = { type: 9, pageID: 1, searchBy: "", fromdt: "1900-01-01", todt: new Date().toISOString().slice(0, 10), print: 1 };
        axios.post(HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`, collectData)
            .then((res) => {
                const data = res?.data?.data?.departmentwiseCountRest; // Check path based on actual API response
                if (data && Array.isArray(data)) {
                    setDeptCountRawData(data);
                } else {
                    console.warn("API did not return expected department count data format:", res?.data);
                    setDeptCountRawData([]);
                    setDeptChartError("Invalid data format received for department chart.");
                }
            })
            .catch(error => {
                console.error("Error fetching department count chart:", error);
                setDeptChartError("Failed to load department chart data.");
                setDeptCountRawData([]);
            })
            .finally(() => setIsDeptChartLoading(false));
    };

    const devId = getSecureDevId();
    const getDocEntryAndShowModal = () => {
        setIsLoadingModal(true);
        setModalError(null);
        setModalData([]);
        setOpenRowId(null);
        setSearchQuery("");
        setModalPage(1); // Reset to first page
        setIsModalOpen(true);
        const collectData = {
            docMid: -1,
            fileTypId: -1,
            //@ts-ignore
            divisionid: devId === "null" ? -1 : devId,
            subsubjId: -1,
            user_Id: -1,
            fileNo: "",
            subDeptID: -1,
            complt: ""
        };
        axios.post(HOST_URL.HOST_URL2 + `DocMangr/GetDocMangr`, collectData)
            .then((res) => {
                if (res?.data?.isSuccess && Array.isArray(res.data.data)) {
                    setModalData(res.data.data);
                } else {
                    setModalError(res?.data?.mesg || "Failed to fetch data or invalid format received.");
                    setModalData([]);
                }
            })
            .catch((error) => {
                console.error("Error fetching document entry data:", error);
                setModalError("An error occurred while fetching document details.");
                setModalData([]);
            })
            .finally(() => setIsLoadingModal(false));
    };

    const getDocTypeAndShowModal = () => {
        setIsLoadingDocTypeModal(true);
        setDocTypeModalError(null);
        setDocTypeData([]);
        setDocTypeSearchQuery("");
        setDocTypePage(1); // Reset to first page
        setIsDocTypeModalOpen(true);
        // Ensure userId is handled appropriately (e.g., from context/auth or default)
        const collectData = { docID: -1, parentDocId: 0, userId: "" }; // Consider passing actual userId if needed
        axios.post(HOST_URL.HOST_URL2 + `OtherDocType/GetOtherDocType`, collectData)
            .then((res) => {
                if (res?.data?.isSuccess && Array.isArray(res.data.data)) {
                    setDocTypeData(res.data.data);
                } else {
                    setDocTypeModalError(res?.data?.mesg || "Failed to fetch document types or invalid format received.");
                    setDocTypeData([]);
                }
            })
            .catch((error) => {
                console.error("Error fetching document type data:", error);
                setDocTypeModalError("An error occurred while fetching document types.");
                setDocTypeData([]);
            })
            .finally(() => setIsLoadingDocTypeModal(false));
    };


    // --- Event Handlers ---
    const handleDepartmentClick = async (dept: any) => {
        const deptId = dept?.id;
        const deptName = dept?.name;
        if (!deptId || !deptName) return;

        setSelectedDepartment(deptName);
        setSelectedDepartmentId(deptId);
        setIsLoadingDeptData(true);
        setDeptDataError(null);
        setSelectedDeptData([]);
        setDeptDataSearchQuery("");
        setOpenDrawerRowId(null);
        setDrawerPage(1); // Reset to first page
        setIsDeptDrawerOpen(true); // Open the drawer

        const collectData = {
            docMid: -1,
            fileTypId: -1,
            divisionid: deptId,
            subsubjId: -1,
            user_Id: -1,
            fileNo: "",
            subDeptID: -1,
            complt: "",
        };

        try {
            const resp = await axios.post(HOST_URL.HOST_URL2 + `DocMangr/GetDocMangr`, collectData);
            if (resp?.data?.isSuccess && Array.isArray(resp.data.data)) {
                setSelectedDeptData(resp.data.data);
            } else {
                console.warn("Department data fetch warning:", resp?.data?.mesg);
                setDeptDataError(resp?.data?.mesg || `No documents found for ${deptName}.`);
                setSelectedDeptData([]);
            }
        } catch (error) {
            console.error(`Error fetching data for department ${deptName} (ID: ${deptId}):`, error);
            setDeptDataError(`An error occurred while fetching documents for ${deptName}.`);
            setSelectedDeptData([]);
        } finally {
            setIsLoadingDeptData(false);
        }
    };

    const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (event && event.type === 'keydown' &&
            ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) {
            return;
        }
        setIsDeptDrawerOpen(open);
        if (!open) {
            setSelectedDepartment(null);
            setSelectedDepartmentId(null);
            setDeptDataSearchQuery("");
            setOpenDrawerRowId(null);
            setDrawerPage(1); // Reset pagination when closing
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSearchQuery("");
        setOpenRowId(null);
        setModalPage(1); // Reset pagination
    };

    const handleCloseDocTypeModal = () => {
        setIsDocTypeModalOpen(false);
        setDocTypeSearchQuery("");
        setDocTypePage(1); // Reset pagination
    };

    const handleCloseTotalFilesModal = () => {
        setIsTotalFilesModalOpen(false);
        setTotalFilesSearchQuery("");
        setTotalFilesPage(1); // Reset pagination
    };

    // --- Filtered Data ---
    const filteredModalData = useMemo(() => {
        if (!searchQuery) return modalData;
        const lowerCaseQuery = searchQuery.toLowerCase();
        return modalData.filter(doc =>
            (doc.fileNo?.toLowerCase().includes(lowerCaseQuery)) ||
            (doc.fileDef?.toLowerCase().includes(lowerCaseQuery)) ||
            (doc.department_Name?.toLowerCase().includes(lowerCaseQuery)) ||
            (doc.subj?.toLowerCase().includes(lowerCaseQuery)) ||
            (String(doc.docMid)?.toLowerCase().includes(lowerCaseQuery))
        );
    }, [modalData, searchQuery]);

    const filteredDocTypeData = useMemo(() => {
        if (!docTypeSearchQuery) return docTypeData;
        const lowerCaseQuery = docTypeSearchQuery.toLowerCase();
        return docTypeData.filter(docType =>
            (docType.doc_Name?.toLowerCase().includes(lowerCaseQuery)) ||
            (docType.doc_shortname?.toLowerCase().includes(lowerCaseQuery)) ||
            (docType.parentDoc_Name?.toLowerCase().includes(lowerCaseQuery)) ||
            (docType.session_year?.toLowerCase().includes(lowerCaseQuery))
        );
    }, [docTypeData, docTypeSearchQuery]);

    const filteredTotalFilesData = useMemo(() => {
        if (!totalFilesSearchQuery || !Array.isArray(isIndex)) return isIndex || []; // Ensure return array
        const lowerCaseQuery = totalFilesSearchQuery.toLowerCase();
        // Add optional chaining for safety
        return (isIndex || []).filter(item =>
            item?.fileName?.toLowerCase().includes(lowerCaseQuery)
        );
    }, [isIndex, totalFilesSearchQuery]);

    const filteredSelectedDeptData = useMemo(() => {
        if (!deptDataSearchQuery) return selectedDeptData;
        const lowerCaseQuery = deptDataSearchQuery.toLowerCase();
        return selectedDeptData.filter(doc =>
            (doc.fileNo?.toLowerCase().includes(lowerCaseQuery)) ||
            (doc.fileDef?.toLowerCase().includes(lowerCaseQuery)) ||
            (doc.subj?.toLowerCase().includes(lowerCaseQuery)) ||
            (String(doc.docMid)?.toLowerCase().includes(lowerCaseQuery))
        );
    }, [selectedDeptData, deptDataSearchQuery]);

    // --- Chart Options ---
    const optionsDeptCountChart = {
        chartArea: { width: '85%', height: '70%' },
        hAxis: { title: 'Department', textStyle: { fontSize: 10, slantedText: true, slantedTextAngle: 35 } },
        vAxis: {
            title: 'Total Count',
            minValue: 0,
            textStyle: { fontSize: 10 },
            gridlines: { color: '#eee' }
        },
        legend: { position: 'none' } as const,
        bar: { groupWidth: '75%' },
        animation: { startup: true, duration: 1200, easing: 'out' as const },
        tooltip: { isHtml: true },
        colors: [theme.palette.secondary.main],
        annotations: {
            textStyle: {
                fontSize: 11,
                bold: false,
                color: '#333333',
                auraColor: 'none'
            },
            alwaysOutside: true,
            stem: {
                color: 'transparent',
                length: 0
            }
        }
    };

    // --- Styles ---
    const cardHoverStyle = {
        transition: theme.transitions.create(['transform', 'box-shadow', 'background-color'], {
            duration: theme.transitions.duration.short, // Use theme transition duration
        }),
        '&:hover': {
            transform: 'scale(1.03)', // Slightly larger pop
            boxShadow: theme.shadows[10], // More pronounced shadow
            // Optional: subtle background tint on hover
            // backgroundColor: alpha(theme.palette.primary.light, 0.05),
        },
    };

    // --- Stat Items Array (with icons and colors) ---
    const statItems = [
        {
            // title: "Total Document",
            title: t("text.totalDoc"),
            count: TotalCount,
            icon: DescriptionOutlinedIcon,
            color: theme.palette.primary.main,
            contrastColor: theme.palette.primary.contrastText,
            onClick: getDocEntryAndShowModal
        },
        {
            // title: "Total Type",
            title: t("text.totalType"),
            count: FileCount,
            icon: CategoryOutlinedIcon,
            color: theme.palette.secondary.main,
            contrastColor: theme.palette.secondary.contrastText,
            onClick: getDocTypeAndShowModal
        },
        {
            // title: "Total Index File",
            title: t("text.totalIndexFile"),
            count: IndexCount,
            icon: FolderZipOutlinedIcon,
            color: theme.palette.success.dark,
            contrastColor: theme.palette.success.contrastText,
            onClick: () => getIndexOrSplitData(11)
        },

        {
            // title: "Pending Indexing",
            title: t("text.pendingIndexing"),
            count: SplitCount,
            icon: CallSplitOutlinedIcon,
            color: theme.palette.info.main,
            contrastColor: theme.palette.info.contrastText,
            onClick: () => getIndexOrSplitData(12)
        }
    ];


    // --- Render ---
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)' /* Adjust if needed */ }}>

            {/* Main Content Area */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    pt: { xs: 4, sm: 10, md: 2 },  // 👈 IMPORTANT (mobile ke liye)
                    px: { xs: 1.5, sm: 2, md: 3 },
                    pb: { xs: 2, sm: 3 },
                    backgroundColor: theme.palette.background.default // Use theme background
                }}
            >
                <Grid container spacing={{ xs: 2, md: 3 }} >

                    {/* === REVISED Stat Cards Section === */}
                    {statItems.map((item: any, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Card
                                elevation={5} // Slightly increased elevation
                                sx={{
                                    borderRadius: '16px', // More rounded corners
                                    minHeight: { xs: 'auto', sm: '100px' },
                                    display: 'flex', // Use flex for content alignment
                                    overflow: 'hidden', // Prevent content spillover
                                    cursor: item.onClick ? 'pointer' : 'default',
                                    backgroundColor: theme.palette.background.paper, // Use paper background
                                    ...cardHoverStyle, // Apply hover style
                                }}
                                onClick={item.onClick}
                            >
                                <CardContent sx={{
                                    display: 'flex',
                                    alignItems: 'center', // Vertically center content
                                    justifyContent: 'space-between', // Space out icon and text
                                    p: { xs: 1.5, sm: 2, md: 2.5 },
                                    width: '100%', // Ensure content takes full width
                                    gap: { xs: 1, sm: 1.5, md: 2 },
                                }}>
                                    {/* Icon Side */}
                                    <Avatar
                                        sx={{
                                            bgcolor: item.color,
                                            color: item.contrastColor,
                                            width: { xs: 45, sm: 60 },
                                            height: { xs: 45, sm: 60 },
                                            boxShadow: `0 4px 12px -2px ${item.color}aa` // Soft shadow matching the color
                                        }}
                                    >
                                        <item.icon sx={{ fontSize: '2.2rem' }} /> {/* Larger Icon */}
                                    </Avatar>

                                    {/* Text Side */}
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-end', // Align text to the right
                                        textAlign: 'right',
                                        overflow: 'hidden', // Prevent text overflow issues
                                    }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                mb: 0.5,
                                                fontSize: { xs: '10px', sm: '12px', md: '14px' },
                                                textAlign: 'right',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                width: '100%',
                                            }}
                                        >
                                            {item.title}
                                        </Typography>
                                        <Typography
                                            variant="h4" // Large, prominent count
                                            fontWeight="bold"
                                            color="text.primary"
                                            sx={{
                                                fontWeight: 'bold',
                                                fontSize: { xs: '20px', sm: '28px' },
                                                lineHeight: 1.2
                                            }} // Adjust line height
                                        >
                                            <CountUp end={item.count} duration={1.5} separator="," />
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}


                    {/* Department List Card */}
                    <Grid item xs={12} md={4}>
                        <Card elevation={3} sx={{ borderRadius: '12px', height: '400px', display: 'flex', flexDirection: 'column' }}>
                            <CardHeader
                                title={t("text.selectDepartment")}
                                titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                                sx={{
                                    background: `linear-gradient(135deg, ${theme.palette.warning.light} 0%, ${theme.palette.warning.main} 100%)`,
                                    color: theme.palette.warning.contrastText,
                                    py: 1.5,
                                    flexShrink: 0
                                }}
                            />

                            {/* Search Field */}
                            <Box sx={{ p: 1.5, pb: 0, flexShrink: 0 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    placeholder={t("text.searchDepartment")}
                                    onChange={(e) => {
                                        const searchTerm = e.target.value.toLowerCase();
                                        if (searchTerm === '') {
                                            setFilteredDepartmentList(DepartmentList);
                                        } else {
                                            const filtered = DepartmentList.filter(dept =>
                                                dept.name.toLowerCase().includes(searchTerm)
                                            );
                                            setFilteredDepartmentList(filtered);
                                        }
                                    }}
                                    sx={{
                                        backgroundColor: 'white',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px'
                                        }
                                    }}
                                />
                            </Box>

                            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1, pt: 0 }}>
                                <List component="nav" dense>
                                    {filteredDepartmentList.length > 0 ? (
                                        filteredDepartmentList.map((department) => (
                                            <ListItemButton
                                                key={department.id}
                                                selected={selectedDepartmentId === department.id}
                                                onClick={() => handleDepartmentClick(department)}
                                                sx={{
                                                    borderRadius: '8px',
                                                    my: 0.5,
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme.palette.primary.main,
                                                        color: theme.palette.primary.contrastText,
                                                        '&:hover': { backgroundColor: theme.palette.primary.dark }
                                                    },
                                                    '&:hover': { backgroundColor: theme.palette.action.hover },
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <ListItemText
                                                    primary={department.name}
                                                    primaryTypographyProps={{
                                                        fontWeight: 500,
                                                        textAlign: 'center',
                                                        sx: {
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }
                                                    }}
                                                />
                                            </ListItemButton>
                                        ))
                                    ) : (
                                        <ListItem>
                                            <ListItemText
                                                primary={t("text.noDepartmentFound")}
                                                sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}
                                            />
                                        </ListItem>
                                    )}
                                </List>
                            </Box>
                        </Card>
                    </Grid>

                    {/* Department Wise Count Chart */}
                    <Grid item xs={12} md={8}>
                        <Card
                            elevation={3}
                            sx={{
                                borderRadius: '16px',
                                height: '400px',   // ✅ Match Department List height
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%'
                            }}
                        >
                            <CardHeader
                                // title="Department Wise Document Counts"
                                title={t("text.departmentWiseDocCount")}
                                titleTypographyProps={{
                                    variant: 'h6',
                                    fontWeight: 'bold'
                                }}
                                sx={{
                                    background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
                                    color: theme.palette.secondary.contrastText,
                                    py: 1.5,
                                    flexShrink: 0
                                }}
                            />
                            <CardContent
                                sx={{
                                    flexGrow: 1,
                                    p: 1.5,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    overflow: 'hidden',
                                    width: '100%'
                                }}
                            >
                                {isDeptChartLoading ? (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 2,
                                        width: '100%',
                                        height: '100%',
                                        justifyContent: 'center'
                                    }}>
                                        <CircularProgress color="secondary" size={40} />
                                        <Typography variant="body2" color="text.secondary">
                                            Loading chart data...
                                        </Typography>
                                    </Box>
                                ) : deptChartError ? (
                                    <Alert severity="error" sx={{ width: '90%', m: 1 }}>
                                        {deptChartError}
                                    </Alert>
                                ) : chartOptions && chartSeries.length > 0 ? (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            position: 'relative'
                                        }}
                                    >
                                        <ReactApexChart
                                            options={chartOptions}
                                            series={chartSeries}
                                            type="bar"
                                            height="100%"
                                            width="100%"
                                        />
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '100%',
                                            height: '100%'
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{ textAlign: 'center', color: 'text.secondary', p: 2 }}
                                        >
                                            No department count data available.
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                </Grid>
            </Box> {/* End Main Content Area */}

            {/* --- Document Details Modal --- */}
            <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="xl" fullWidth scroll="paper" PaperProps={{ sx: { maxHeight: '90vh', height: 'calc(100% - 64px)' } }}>
                <DialogTitle sx={{ bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, py: 1.5, fontSize: '1.1rem', m: 0, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    {t("text.documentDetails")}
                </DialogTitle>
                <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.grey[50], position: 'sticky', top: 0, zIndex: 1101 }}>
                    <TextField fullWidth size="small" variant="outlined" label={t("text.searchDocuments")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ backgroundColor: 'white' }} />
                </Box>
                {isLoadingModal && <LinearProgress color="primary" sx={{ position: 'sticky', top: 57, zIndex: 1101 }} />}
                <DialogContent dividers sx={{ p: 0, '&.MuiDialogContent-root': { padding: 0 } }}>
                    {modalError ? (
                        <Box sx={{ p: 3 }}><Alert severity="error">{modalError}</Alert></Box>
                    ) : (
                        <>
                            <TableContainer component={Paper} elevation={0} square sx={{ maxHeight: 'calc(100vh - 64px - 58px - 53px)' /* Adjust */ }}>
                                <Table stickyHeader aria-label="collapsible document table" size="small">
                                    <TableHead>
                                        <TableRow sx={{ '& th': { backgroundColor: theme.palette.grey[200], fontWeight: 'bold', color: theme.palette.text.primary, padding: '6px 10px', position: 'sticky', top: 0, zIndex: 1 } }}>
                                            <TableCell sx={{ width: '50px' }} />
                                            {/* <TableCell>File No</TableCell>
                                            <TableCell>File Name</TableCell>
                                            <TableCell>Department</TableCell>
                                            {/* <TableCell>Subject</TableCell> */}
                                            {/* <TableCell>Date</TableCell>
                                            <TableCell>Files</TableCell>
                                            <TableCell align="center">Preview</TableCell> */}
                                            <TableCell>{t("text.fileNo")}</TableCell>
                                            <TableCell>{t("text.fileName")}</TableCell>
                                            <TableCell>{t("text.department")}</TableCell>
                                            <TableCell>{t("text.date")}</TableCell>
                                            <TableCell>{t("text.files")}</TableCell>
                                            <TableCell>{t("text.Download")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {isLoadingModal && !modalData.length ? (
                                            <TableRow><TableCell colSpan={7} align="center" sx={{ p: 5 }}><CircularProgress /></TableCell></TableRow>
                                        ) : filteredModalData.length > 0 ? (
                                            filteredModalData
                                                .slice((modalPage - 1) * modalRowsPerPage, modalPage * modalRowsPerPage)
                                                .map((doc, index) => (
                                                    <DocRow
                                                        key={doc.docMid}
                                                        doc={doc}
                                                        isOpen={openRowId === doc.docMid}
                                                        onToggle={() => setOpenRowId(openRowId === doc.docMid ? null : doc.docMid)}
                                                        formatDate={formatDate}
                                                        theme={theme}
                                                        isEvenRow={index % 2 === 0}
                                                    />
                                                ))
                                        ) : (
                                            <TableRow><TableCell colSpan={7} align="center"><Typography color="textSecondary" sx={{ p: 3, fontStyle: 'italic' }}>{searchQuery ? "No documents match your search criteria." : "No document details found."}</Typography></TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination for Document Modal */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    py: 2,
                                    px: 2,
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                    backgroundColor: theme.palette.background.paper
                                }}
                            >
                                <Typography variant="body2" fontWeight="bold">
                                    {t("text.totalRecords")}: {filteredModalData.length}
                                </Typography>

                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <IconButton
                                        color="primary"
                                        disabled={modalPage === 1}
                                        onClick={() => setModalPage(prev => prev - 1)}
                                    >
                                        <ChevronLeftIcon />
                                    </IconButton>

                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {modalPage} / {Math.ceil(filteredModalData.length / modalRowsPerPage) || 1}
                                    </Typography>

                                    <IconButton
                                        color="primary"
                                        disabled={modalPage >= Math.ceil(filteredModalData.length / modalRowsPerPage)}
                                        onClick={() => setModalPage(prev => prev + 1)}
                                    >
                                        <ChevronRightIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 1.5, borderTop: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.grey[50] }}>
                    <Button onClick={handleCloseModal} variant="contained" color="primary" size="medium">{t("text.close")}</Button>
                </DialogActions>
            </Dialog>

            {/* --- Document Type Modal --- */}
            <Dialog open={isDocTypeModalOpen} onClose={handleCloseDocTypeModal} maxWidth="lg" fullWidth scroll="paper" PaperProps={{ sx: { maxHeight: '85vh', height: 'calc(100% - 64px)' } }}>
                <DialogTitle sx={{ bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, py: 1.5, fontSize: '1.1rem', m: 0, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    {t("text.documentTypes")}
                </DialogTitle>
                <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.grey[50], position: 'sticky', top: 0, zIndex: 1101 }}>
                    <TextField fullWidth size="small" variant="outlined" label={t("text.searchDocTypes")} value={docTypeSearchQuery} onChange={(e) => setDocTypeSearchQuery(e.target.value)} sx={{ backgroundColor: 'white' }} />
                </Box>
                {isLoadingDocTypeModal && <LinearProgress color="secondary" sx={{ position: 'sticky', top: 57, zIndex: 1101 }} />}
                <DialogContent dividers sx={{ p: 0, '&.MuiDialogContent-root': { padding: 0 } }}>
                    {docTypeModalError ? (
                        <Box sx={{ p: 3 }}><Alert severity="error">{docTypeModalError}</Alert></Box>
                    ) : (
                        <>
                            <TableContainer component={Paper} elevation={0} square sx={{ maxHeight: 'calc(100vh - 64px - 58px - 53px)' /* Adjust */ }}>
                                <Table stickyHeader aria-label="document type table" size="small">
                                    <TableHead>
                                        <TableRow sx={{ '& th': { backgroundColor: theme.palette.grey[200], fontWeight: 'bold', color: theme.palette.text.primary, padding: '6px 10px', position: 'sticky', top: 0, zIndex: 1 } }}>
                                            {/* <TableCell>Name</TableCell>
                                            <TableCell>Short Name</TableCell>
                                            <TableCell>Parent Name</TableCell>
                                            <TableCell align="right">Entry Date</TableCell> */}
                                            <TableCell>{t("text.name")}</TableCell>
                                            <TableCell>{t("text.shortName")}</TableCell>
                                            <TableCell>{t("text.parentName")}</TableCell>
                                            <TableCell align="right">{t("text.entryDate")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {isLoadingDocTypeModal && !docTypeData.length ? (
                                            <TableRow><TableCell colSpan={4} align="center" sx={{ p: 5 }}><CircularProgress color="secondary" /></TableCell></TableRow>
                                        ) : filteredDocTypeData.length > 0 ? (
                                            filteredDocTypeData
                                                .slice((docTypePage - 1) * docTypeRowsPerPage, docTypePage * docTypeRowsPerPage)
                                                .map((docType, index) => (
                                                    <TableRow key={docType.docID || `doctype-${index}`} sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover }, '&:last-child td, &:last-child th': { border: 0 }, }}>
                                                        <TableCell sx={{ padding: '6px 10px' }}>{docType.doc_Name || '-'}</TableCell>
                                                        <TableCell sx={{ padding: '6px 10px' }}>{docType.doc_shortname || '-'}</TableCell>
                                                        <TableCell sx={{ padding: '6px 10px' }}>{docType.parentDoc_Name || '-'}</TableCell>
                                                        <TableCell align="right" sx={{ padding: '6px 10px' }}>{formatDate(docType.entry_Date)}</TableCell>
                                                    </TableRow>
                                                ))
                                        ) : (
                                            <TableRow><TableCell colSpan={4} align="center"><Typography color="textSecondary" sx={{ p: 3, fontStyle: 'italic' }}>{docTypeSearchQuery ? "No document types match your search criteria." : "No document types found."}</Typography></TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination for Document Type Modal */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    py: 2,
                                    px: 2,
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                    backgroundColor: theme.palette.background.paper
                                }}
                            >
                                <Typography variant="body2" fontWeight="bold">
                                    {t("text.totalRecords")}: {filteredDocTypeData.length}
                                </Typography>

                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <IconButton
                                        color="primary"
                                        disabled={docTypePage === 1}
                                        onClick={() => setDocTypePage(prev => prev - 1)}
                                    >
                                        <ChevronLeftIcon />
                                    </IconButton>

                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {docTypePage} / {Math.ceil(filteredDocTypeData.length / docTypeRowsPerPage) || 1}
                                    </Typography>

                                    <IconButton
                                        color="primary"
                                        disabled={docTypePage >= Math.ceil(filteredDocTypeData.length / docTypeRowsPerPage)}
                                        onClick={() => setDocTypePage(prev => prev + 1)}
                                    >
                                        <ChevronRightIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 1.5, borderTop: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.grey[50] }}>
                    <Button onClick={handleCloseDocTypeModal} variant="contained" color="primary" size="medium">{t("text.close")}</Button>
                </DialogActions>
            </Dialog>

            {/* --- Total Index/Split Files Modal --- */}

            <Dialog
                open={isIndexSplitModalOpen}
                onClose={handleCloseIndexSplitModal}
                maxWidth="xl"
                fullWidth
                scroll="paper"
                PaperProps={{
                    sx: { maxHeight: '90vh', height: 'calc(100% - 64px)' }
                }}
            >

                <DialogTitle
                    sx={{
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        py: 1.5,
                        fontSize: '1.1rem',
                        m: 0,
                        borderBottom: `1px solid ${theme.palette.divider}`
                    }}
                >

                    {indexSplitType === "index"
                        ? t("text.splitFiles")
                        : t("text.pendingSplitFiles")}

                </DialogTitle>


                {/* SEARCH BAR */}

                <Box
                    sx={{
                        p: 1.5,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.grey[50],
                        position: 'sticky',
                        top: 0,
                        zIndex: 1101
                    }}
                >

                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        // label="Search File No, File Name, Department"
                        label={t("text.searchFile")}
                        value={indexSplitSearch}
                        onChange={(e) => setIndexSplitSearch(e.target.value)}
                        sx={{ backgroundColor: 'white' }}
                    />

                </Box>


                {/* LOADER */}

                {isIndexSplitLoading &&
                    <LinearProgress
                        sx={{ position: 'sticky', top: 57, zIndex: 1101 }}
                    />
                }


                <DialogContent
                    dividers
                    sx={{ p: 0, '&.MuiDialogContent-root': { padding: 0 } }}
                >

                    <TableContainer
                        component={Paper}
                        elevation={0}
                        square
                        sx={{
                            maxHeight: 'calc(100vh - 64px - 58px - 53px)'
                        }}
                    >

                        <Table stickyHeader size="small">

                            <TableHead>

                                <TableRow
                                    sx={{
                                        '& th': {
                                            backgroundColor: theme.palette.grey[200],
                                            fontWeight: 'bold',
                                            color: theme.palette.text.primary,
                                            padding: '6px 10px',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1
                                        }
                                    }}
                                >

                                    {/* <TableCell>File No</TableCell>
                                    <TableCell>File Name</TableCell>
                                    <TableCell>Department</TableCell>
                                    <TableCell>Status</TableCell> */}
                                    <TableCell>{t("text.fileNo")}</TableCell>
                                    <TableCell>{t("text.fileName")}</TableCell>
                                    <TableCell>{t("text.department")}</TableCell>
                                    <TableCell>{t("text.status")}</TableCell>

                                </TableRow>

                            </TableHead>


                            <TableBody>

                                {isIndexSplitLoading && !indexSplitData.length ? (

                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ p: 5 }}>
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>

                                ) : filteredIndexSplitData.length > 0 ? (

                                    filteredIndexSplitData
                                        .slice(
                                            (indexSplitPage - 1) * indexSplitRowsPerPage,
                                            indexSplitPage * indexSplitRowsPerPage
                                        )
                                        .map((row: any, index: number) => (

                                            <TableRow
                                                key={row.docMid}
                                                sx={{
                                                    '&:nth-of-type(odd)': {
                                                        backgroundColor: theme.palette.action.hover
                                                    },
                                                    '&:last-child td': { border: 0 }
                                                }}
                                            >

                                                <TableCell sx={{ padding: '6px 10px' }}>
                                                    {row.fileNo || '-'}
                                                </TableCell>

                                                <TableCell sx={{ padding: '6px 10px' }}>
                                                    {row.fileDef || '-'}
                                                </TableCell>

                                                <TableCell sx={{ padding: '6px 10px' }}>
                                                    {row.departmentName || '-'}
                                                </TableCell>

                                                <TableCell sx={{ padding: '6px 10px' }}>

                                                    <Chip
                                                        label={row.indexStatus}
                                                        color={
                                                            row.indexStatus === "Indexed"
                                                                ? "success"
                                                                : "warning"
                                                        }
                                                    />

                                                </TableCell>

                                            </TableRow>

                                        ))

                                ) : (

                                    <TableRow>

                                        <TableCell colSpan={4} align="center">

                                            <Typography
                                                color="textSecondary"
                                                sx={{ p: 3, fontStyle: 'italic' }}
                                            >

                                                {indexSplitSearch
                                                    ? t("text.noRecord")
                                                    : t("text.noRecord")}

                                            </Typography>

                                        </TableCell>

                                    </TableRow>

                                )}

                            </TableBody>

                        </Table>

                    </TableContainer>


                    {/* PAGINATION */}

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 2,
                            px: 2,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper
                        }}
                    >

                        <Typography variant="body2" fontWeight="bold">

                            {t("text.totalRecords")}: {filteredIndexSplitData.length}

                        </Typography>


                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

                            <IconButton
                                color="primary"
                                disabled={indexSplitPage === 1}
                                onClick={() => setIndexSplitPage(p => p - 1)}
                            >

                                <ChevronLeftIcon />

                            </IconButton>


                            <Typography variant="body2" sx={{ fontWeight: 600 }}>

                                {indexSplitPage} /
                                {Math.ceil(filteredIndexSplitData.length / indexSplitRowsPerPage) || 1}

                            </Typography>


                            <IconButton
                                color="primary"
                                disabled={
                                    indexSplitPage >= Math.ceil(filteredIndexSplitData.length / indexSplitRowsPerPage)
                                }
                                onClick={() => setIndexSplitPage(p => p + 1)}
                            >

                                <ChevronRightIcon />

                            </IconButton>

                        </Box>

                    </Box>

                </DialogContent>


                <DialogActions
                    sx={{
                        p: 1.5,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.grey[50]
                    }}
                >

                    <Button
                        onClick={handleCloseIndexSplitModal}
                        variant="contained"
                        color="primary"
                        size="medium"
                    >

                        {t("text.close")}

                    </Button>

                </DialogActions>

            </Dialog>

            {/* --- Department Documents Swipeable Drawer (Right Side with Enhanced Table) --- */}
            <SwipeableDrawer
                anchor="right"
                open={isDeptDrawerOpen}
                onClose={toggleDrawer(false)}
                onOpen={toggleDrawer(true)}
                disableBackdropTransition={!iOS}
                disableDiscovery={iOS}
                PaperProps={{
                    sx: {
                        width: { xs: '95%', sm: '85%', md: '75%', lg: '65%' }, // Adjusted width
                        maxWidth: '1100px', // Adjusted max width
                        height: '100vh',
                        borderTopLeftRadius: 16,
                        borderBottomLeftRadius: 16,
                        borderTopRightRadius: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: theme.shadows[8],
                        backgroundColor: theme.palette.background.default,
                    }
                }}
                sx={{
                    zIndex: theme.zIndex.drawer + 1 // Ensure it's above other elements
                }}
            >
                {/* --- Drawer Header --- */}
                <Box sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    flexShrink: 0, // Prevent header from shrinking
                }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                        {selectedDepartment ? `${selectedDepartment}` : 'Department'} Documents
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={toggleDrawer(false)}
                        sx={{ color: theme.palette.primary.contrastText }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* --- Drawer Search Bar --- */}
                <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper, flexShrink: 0 }}>
                    <TextField
                        fullWidth size="small" variant="outlined"
                        // placeholder="Search by File No, PDF Name, Subject..."
                        placeholder={t("text.searchFile")}

                        value={deptDataSearchQuery}
                        onChange={(e) => setDeptDataSearchQuery(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                            backgroundColor: 'white' // Added background color for consistency
                        }}
                    />
                </Box>

                {/* --- Loading Bar --- */}
                {isLoadingDeptData && <LinearProgress color="primary" sx={{ width: '100%', flexShrink: 0 }} />}

                {/* --- Drawer Content - Scrollable Table --- */}
                <Box sx={{ flexGrow: 1, overflow: 'hidden', p: 0, backgroundColor: theme.palette.background.paper, display: 'flex', flexDirection: 'column' }}> {/* Container for table */}
                    {deptDataError ? (
                        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Alert severity="warning" sx={{ width: '90%' }}>{deptDataError}</Alert>
                        </Box>
                    ) : (
                        <>
                            <TableContainer component={Paper} elevation={0} square sx={{ flexGrow: 1, overflowY: 'auto' }}>
                                <Table stickyHeader aria-label="collapsible department document table" size="small">
                                    <TableHead>
                                        <TableRow sx={{ '& th': { backgroundColor: theme.palette.grey[200], fontWeight: 'bold', color: theme.palette.text.primary, padding: '6px 10px', position: 'sticky', top: 0, zIndex: 1 } }}>
                                            <TableCell sx={{ width: '50px' }} />
                                            {/* <TableCell>File No</TableCell>
                                            <TableCell>File Name</TableCell>
                                            <TableCell>Department</TableCell>
                                        
                                            <TableCell>Date</TableCell>
                                            <TableCell>Files</TableCell>
                                            <TableCell align="center">Preview</TableCell> */}
                                            <TableCell>{t("text.fileNo")}</TableCell>
                                            <TableCell>{t("text.fileName")}</TableCell>
                                            <TableCell>{t("text.department")}</TableCell>
                                            <TableCell>{t("text.date")}</TableCell>
                                            <TableCell>{t("text.files")}</TableCell>
                                            <TableCell>{t("text.Download")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {isLoadingDeptData && !selectedDeptData.length ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center" sx={{ p: 5, border: 0 }}> {/* Updated colSpan to 7 */}
                                                    <CircularProgress />
                                                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>Loading documents...</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : filteredSelectedDeptData.length > 0 ? (
                                            filteredSelectedDeptData
                                                .slice((drawerPage - 1) * drawerRowsPerPage, drawerPage * drawerRowsPerPage)
                                                .map((doc, index) => (
                                                    <DocRow
                                                        key={doc.docMid}
                                                        doc={doc}
                                                        isOpen={openDrawerRowId === doc.docMid}
                                                        onToggle={() => setOpenDrawerRowId(openDrawerRowId === doc.docMid ? null : doc.docMid)}
                                                        formatDate={formatDate}
                                                        theme={theme}
                                                        isEvenRow={index % 2 === 0}
                                                    />
                                                ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center" sx={{ p: 4, border: 0 }}> {/* Updated colSpan to 7 */}
                                                    <Typography color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                                        {isLoadingDeptData ? t("text.loading") : (deptDataSearchQuery ? t("text.noDocuments") : `No documents found for ${selectedDepartment}.`)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination for Department Drawer */}
                            {filteredSelectedDeptData.length > 0 && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        py: 2,
                                        px: 2,
                                        borderTop: `1px solid ${theme.palette.divider}`,
                                        backgroundColor: theme.palette.background.paper,
                                        flexShrink: 0
                                    }}
                                >
                                    <Typography variant="body2" fontWeight="bold">
                                        {t("text.totalRecords")}: {filteredSelectedDeptData.length}
                                    </Typography>

                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <IconButton
                                            color="primary"
                                            disabled={drawerPage === 1}
                                            onClick={() => setDrawerPage(prev => prev - 1)}
                                        >
                                            <ChevronLeftIcon />
                                        </IconButton>

                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {drawerPage} / {Math.ceil(filteredSelectedDeptData.length / drawerRowsPerPage) || 1}
                                        </Typography>

                                        <IconButton
                                            color="primary"
                                            disabled={drawerPage >= Math.ceil(filteredSelectedDeptData.length / drawerRowsPerPage)}
                                            onClick={() => setDrawerPage(prev => prev + 1)}
                                        >
                                            <ChevronRightIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </SwipeableDrawer>

        </Box> // End outer Box wrapper
    );
}



// import React, {
//     ReactNode,
//     useEffect,
//     useState,
//     useMemo,
// } from "react";
// import {
//     Card,
//     Grid,
//     CardContent,
//     Typography,
//     Button,
//     Box,
//     CardHeader,
//     List,
//     ListItemButton,
//     ListItemText,
//     useTheme,
//     Dialog,
//     DialogTitle,
//     DialogContent,
//     DialogActions,
//     CircularProgress,
//     Alert,
//     Divider,
//     ListItem,
//     TableRow,
//     TableCell,
//     IconButton,
//     Collapse,
//     TableContainer,
//     Table,
//     TableHead,
//     TableBody,
//     LinearProgress,
//     Paper,
//     TextField,
//     SwipeableDrawer,
//     Chip,
//     Avatar, // <-- Import Avatar
// } from "@mui/material";

// import ReactApexChart from 'react-apexcharts';

// import "./style.css"; // Ensure this file exists or remove the import
// import axios from "axios";
// import HOST_URL from "../../utils/Url"; // Ensure this path is correct
// import { secureGetItem } from "../../utils/encryption"; // Make sure to import at the top

// import CountUp from 'react-countup';
// import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import CloseIcon from '@mui/icons-material/Close';
// import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
// import ChevronRightIcon from '@mui/icons-material/ChevronRight';
// // Import New Icons for Stat Cards
// import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
// import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
// import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
// import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined';
// import DownloadIcon  from '@mui/icons-material/Visibility';
// import { getSecureDevId } from "../../utils/storageHelpers";
// import { useTranslation } from "react-i18next";

// // --- Interfaces ---
// interface DocRowProps {
//     doc: any;
//     isOpen: boolean;
//     onToggle: () => void;
//     formatDate: (dateString: string | null | undefined) => string;
//     theme: any;
//     isEvenRow: boolean;
// }

// // --- Helper Functions ---
// const formatDate = (dateString: string | null | undefined): string => {
//     if (!dateString) return '-';
//     try {
//         const date = new Date(dateString);
//         if (isNaN(date.getTime())) {
//             // Try parsing common non-standard formats if necessary
//             // Example: DD/MM/YYYY or MM-DD-YYYY - handle with a library like moment.js or date-fns for robustness
//             console.warn("Could not parse date directly:", dateString);
//             return 'Invalid Date';
//         }
//         const returnDate = date.toLocaleDateString(undefined, { // Use locale default format
//             year: 'numeric',
//             month: '2-digit',
//             day: '2-digit'
//         });
//         return returnDate.replace(/\//g, '-'); // Replace slashes with dashes for consistency
//     } catch (e) {
//         console.error("Error formatting date:", dateString, e);
//         return 'Invalid Date Format';
//     }
// };


// function DocRow({ doc, isOpen, onToggle, formatDate, theme, isEvenRow }: DocRowProps) {
//     const necessaryFields = {
//         id: doc.docMid,
//         fileNo: doc.fileNo || '-',
//         definition: doc.fileDef || '-',
//         department: doc.department_Name || '-',
//         subject: doc.subj || '-',
//         date: formatDate(doc.fDate),
//         fileCount: doc.member_DocFiles?.length ?? 0,
//         pdfPath: doc.member_DocFiles?.[0]?.pdfPath || null // Get first file's PDF path
//     };

//     // Function to open document preview
//     const handlePreview = (e: React.MouseEvent, pdfPath: string, fileName: string) => {
//         e.stopPropagation(); // Prevent row click
//         if (pdfPath) {
//             window.open(pdfPath, '_blank');
//         } else {
//             alert('Document path not available');
//         }
//     };

//     return (
//         <React.Fragment>
//             <TableRow
//                 sx={{
//                     '& > *': { borderBottom: 'unset' },
//                     backgroundColor: isEvenRow ? theme.palette.action.hover : 'inherit',
//                     '&:hover': { backgroundColor: theme.palette.action.selected },
//                     cursor: 'pointer',
//                 }}
//                 onClick={onToggle}
//             >
//                 <TableCell sx={{ padding: '6px', width: '50px' }}>
//                     <IconButton aria-label="expand row" size="small" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
//                         {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
//                     </IconButton>
//                 </TableCell>
//                 <TableCell sx={{ padding: '6px 10px' }}>{necessaryFields.fileNo}</TableCell>
//                 <TableCell sx={{ padding: '6px 10px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={necessaryFields.definition}>{necessaryFields.definition}</TableCell>
//                 <TableCell sx={{ padding: '6px 10px' }}>{necessaryFields.department}</TableCell>
//                 <TableCell sx={{ padding: '6px 10px' }}>{necessaryFields.date}</TableCell>
//                 <TableCell sx={{ padding: '6px 10px' }}>{necessaryFields.fileCount}</TableCell>
//                 <TableCell sx={{ padding: '6px 10px' }} align="center">
//                     {necessaryFields.pdfPath ? (
//                         <IconButton
//                             aria-label="preview document"
//                             size="small"
//                             onClick={(e) => handlePreview(e, necessaryFields.pdfPath, necessaryFields.fileNo)}
//                             sx={{
//                                 color: theme.palette.primary.main,
//                                 '&:hover': {
//                                     backgroundColor: theme.palette.primary.light + '20',
//                                 }
//                             }}
//                         >
//                             <DownloadIcon  />
//                         </IconButton>
//                     ) : (
//                         <Typography variant="caption" color="textSecondary">-</Typography>
//                     )}
//                 </TableCell>
//             </TableRow>
//             <TableRow>
//                 <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
//                     <Collapse in={isOpen} timeout="auto" unmountOnExit>
//                         <Box sx={{ margin: 1, p: 2, backgroundColor: theme.palette.grey[100], borderRadius: '4px', border: `1px solid ${theme.palette.divider}` }}>
//                             <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 'medium', color: theme.palette.primary.dark }}>
//                                 Details for Document #{doc.docMid}
//                             </Typography>
//                             <Divider sx={{ my: 1 }} />
//                             <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1, mb: 1 }}>Core Information</Typography>
//                             <Grid container spacing={1.5} sx={{ mb: 2, fontSize: '0.9rem' }}>
//                                 <Grid item xs={12} sm={6} md={4}><Typography variant="body2"><strong>Synopsis:</strong> {doc.synopsis || '-'}</Typography></Grid>
//                                 <Grid item xs={12} sm={6} md={4}><Typography variant="body2"><strong>Completion:</strong> {doc.complt || '-'}</Typography></Grid>
//                                 <Grid item xs={12} sm={6} md={4}><Typography variant="body2"><strong>Auth Person:</strong> {doc.authPersn || '-'}</Typography></Grid>
//                                 <Grid item xs={12} sm={6} md={4}><Typography variant="body2"><strong>Sub-Subject Name:</strong> {doc.sub_Subject_Name || '-'}</Typography></Grid>
//                                 <Grid item xs={12} sm={6} md={4}><Typography variant="body2"><strong>File Type Name:</strong> {doc.fileType_Name || doc.fileType || '-'}</Typography></Grid>
//                                 <Grid item xs={12} sm={6} md={4}><Typography variant="body2"><strong>Period From:</strong> {formatDate(doc.perFr)}</Typography></Grid>
//                                 <Grid item xs={12} sm={6} md={4}><Typography variant="body2"><strong>Period Until:</strong> {formatDate(doc.perUt)}</Typography></Grid>
//                                 <Grid item xs={12} sm={6} md={4}><Typography variant="body2"><strong>Create Date:</strong> {formatDate(doc.createdate)}</Typography></Grid>
//                             </Grid>
//                             <Divider sx={{ my: 1 }} />
//                             <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
//                                 Associated Files ({doc.member_DocFiles?.length ?? 0})
//                             </Typography>
//                             {doc.member_DocFiles && doc.member_DocFiles.length > 0 ? (
//                                 <Grid container spacing={2} sx={{ pt: 1 }}>
//                                     {doc.member_DocFiles.map((file: any, fileIndex: number) => (
//                                         <Grid item xs={12} sm={6} md={3} key={file.pdFid || `file-${fileIndex}`}>
//                                             <Paper elevation={1} sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
//                                                 <Typography variant="body1" component="div" sx={{ fontWeight: 'bold', mb: 0.5, wordBreak: 'break-word' }}>
//                                                     {file.pdfName || 'Unnamed File'} {file.pdFid ? `(ID: ${file.pdFid})` : ''}
//                                                 </Typography>
//                                                 <Divider sx={{ mb: 1 }} />
//                                                 <Box sx={{ fontSize: '0.8rem', flexGrow: 1 }}>
//                                                     <Typography variant="caption" display="block"><strong>Keywords:</strong> {file.keywords || '-'}</Typography>
//                                                     <Typography variant="caption" display="block"><strong>Sub Type:</strong> {file.subFtype || '-'}</Typography>
//                                                     <Typography variant="caption" display="block"><strong>File Date:</strong> {formatDate(file.pDate)}</Typography>
//                                                     <Typography variant="caption" display="block"><strong>Is Main:</strong> {file.isMain ? 'Yes' : 'No'}</Typography>
//                                                     <Typography variant="caption" display="block"><strong>Entry Date:</strong> {formatDate(file.entryDate)}</Typography>
//                                                     <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all', mt: 0.5 }}>
//                                                         <strong>Path:</strong> {file.pdfPath || '-'}
//                                                     </Typography>
//                                                 </Box>
//                                             </Paper>
//                                         </Grid>
//                                     ))}
//                                 </Grid>
//                             ) : (
//                                 <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', mt: 1 }}>No associated files found.</Typography>
//                             )}
//                         </Box>
//                     </Collapse>
//                 </TableCell>
//             </TableRow>
//         </React.Fragment>
//     );
// }

// // Main HomePage Component
// export default function HomePage(): ReactNode {
//     const theme = useTheme();
//     const iOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
//     const [filteredDepartmentList, setFilteredDepartmentList] = useState<any[]>([]);
//     // --- State ---
//     const [TotalCount, setTotalCount] = useState<number>(0);
//     const [IndexCount, setIndexCount] = useState<number>(0);
//     const [FileCount, setFileCount] = useState<number>(0);
//     const [SplitCount, setSplitCount] = useState<number>(0);
//     const [DepartmentList, setDepartmentList] = useState<any[]>([]);
//     const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
//     const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
//     const [deptCountRawData, setDeptCountRawData] = useState<any[]>([]);
//     const [deptCountFormattedData, setDeptCountFormattedData] = useState<any[][]>([]);
//     const [isDeptChartLoading, setIsDeptChartLoading] = useState<boolean>(false);
//     const [deptChartError, setDeptChartError] = useState<string | null>(null);
//     const { i18n, t } = useTranslation();
//     // Modal (Total Docs) state
//     const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
//     const [modalData, setModalData] = useState<any[]>([]);
//     const [isLoadingModal, setIsLoadingModal] = useState<boolean>(false);
//     const [modalError, setModalError] = useState<string | null>(null);
//     const [openRowId, setOpenRowId] = useState<number | null>(null); // For modal rows
//     const [searchQuery, setSearchQuery] = useState<string>("");

//     // Pagination for Document Details Modal
//     const [modalPage, setModalPage] = useState(1);
//     const [modalRowsPerPage] = useState(6); // showing 6 rows

//     // Modal (Doc Type) state
//     const [isDocTypeModalOpen, setIsDocTypeModalOpen] = useState<boolean>(false);
//     const [docTypeData, setDocTypeData] = useState<any[]>([]);
//     const [isLoadingDocTypeModal, setIsLoadingDocTypeModal] = useState<boolean>(false);
//     const [docTypeModalError, setDocTypeModalError] = useState<string | null>(null);
//     const [docTypeSearchQuery, setDocTypeSearchQuery] = useState<string>("");

//     // Pagination for Document Type Modal
//     const [docTypePage, setDocTypePage] = useState(1);
//     const [docTypeRowsPerPage] = useState(6); // showing 10 rows

//     // Modal (Index/Split Files) state
//     const [isIndex, setIndex] = useState<any[]>([]); // Renamed from isIndex to avoid confusion
//     const [isTotalFilesModalOpen, setIsTotalFilesModalOpen] = useState<boolean>(false);
//     const [isLoadingTotalFiles, setIsLoadingTotalFiles] = useState<boolean>(false);
//     const [totalFilesSearchQuery, setTotalFilesSearchQuery] = useState<string>("");

//     // Pagination for Total Files Modal
//     const [totalFilesPage, setTotalFilesPage] = useState(1);
//     const [totalFilesRowsPerPage] = useState(8);

//     // Drawer (Department Docs) state
//     const [isDeptDrawerOpen, setIsDeptDrawerOpen] = useState<boolean>(false);
//     const [selectedDeptData, setSelectedDeptData] = useState<any[]>([]);
//     const [isLoadingDeptData, setIsLoadingDeptData] = useState<boolean>(false);
//     const [deptDataError, setDeptDataError] = useState<string | null>(null);
//     const [deptDataSearchQuery, setDeptDataSearchQuery] = useState<string>("");
//     const [openDrawerRowId, setOpenDrawerRowId] = useState<number | null>(null); // For drawer rows

//     const [indexSplitData, setIndexSplitData] = useState<any[]>([]);
//     const [indexSplitSearch, setIndexSplitSearch] = useState("");
//     const [indexSplitPage, setIndexSplitPage] = useState(1);
//     const [indexSplitRowsPerPage] = useState(6);
//     const [isIndexSplitModalOpen, setIsIndexSplitModalOpen] = useState(false);
//     const [isIndexSplitLoading, setIsIndexSplitLoading] = useState(false);
//     const [indexSplitType, setIndexSplitType] = useState<"index" | "split" | null>(null);

//     // Pagination for Department Drawer
//     const [drawerPage, setDrawerPage] = useState(1);
//     const [drawerRowsPerPage] = useState(9);

//     const [chartOptions, setChartOptions] = useState<any>(null);
//     const [chartSeries, setChartSeries] = useState<any[]>([]);

//     // --- Effects ---
//     useEffect(() => {
//         // getTotalDoc();                     // type:1
//         getDepartmentTotalDocument();      // type:9
//         getDepartmentList();
//         getDepartmentCountChartData();
//     }, []);



//     useEffect(() => {
//         if (deptCountRawData && deptCountRawData.length > 0) {
//             const categories = deptCountRawData.map(item =>
//                 item.divisionName || item.department || 'Unknown Dept'
//             );
//             const seriesData = deptCountRawData.map(item =>
//                 item.totalCount || item.count || 0
//             );

//             setChartOptions({
//                 chart: {
//                     type: 'bar',
//                     height: '100%',
//                     toolbar: {
//                         show: true,
//                         tools: {
//                             download: true,
//                             selection: true,
//                             zoom: true,
//                             zoomin: true,
//                             zoomout: true,
//                             pan: true,
//                             reset: true
//                         }
//                     },
//                     animations: {
//                         enabled: true,
//                         easing: 'easeinout',
//                         speed: 800
//                     },
//                     zoom: {
//                         enabled: true,
//                         type: 'x',
//                         autoScaleYaxis: true
//                     }
//                 },
//                 plotOptions: {
//                     bar: {
//                         borderRadius: 6,
//                         horizontal: false,
//                         columnWidth: '55%',
//                         dataLabels: {
//                             position: 'top'
//                         }
//                     }
//                 },
//                 dataLabels: {
//                     enabled: true,
//                     offsetY: -20,
//                     style: {
//                         fontSize: '12px',
//                         colors: [theme.palette.text.primary]
//                     },
//                     formatter: function (val: number) {
//                         return val.toString();
//                     }
//                 },
//                 xaxis: {
//                     categories: categories,
//                     labels: {
//                         style: {
//                             fontSize: '12px',
//                             fontWeight: 600,
//                             colors: theme.palette.text.primary
//                         },
//                         rotate: -45,
//                         rotateAlways: true,
//                         trim: true,
//                         maxHeight: 120
//                     },
//                     axisBorder: {
//                         show: true,
//                         color: theme.palette.divider,
//                         height: 2
//                     },
//                     axisTicks: {
//                         show: true,
//                         color: theme.palette.divider
//                     },
//                     tooltip: {
//                         enabled: false
//                     }
//                 },
//                 yaxis: {
//                     title: {
//                         text: 'Total Document Count',
//                         style: {
//                             fontSize: '14px',
//                             fontWeight: 600,
//                             color: theme.palette.text.primary
//                         }
//                     },
//                     labels: {
//                         style: {
//                             fontSize: '12px',
//                             colors: theme.palette.text.secondary
//                         },
//                         formatter: function (val: number) {
//                             return val.toFixed(0);
//                         }
//                     },
//                     forceNiceScale: true,
//                     tickAmount: 5
//                 },
//                 grid: {
//                     borderColor: theme.palette.divider,
//                     strokeDashArray: 4,
//                     padding: {
//                         top: 0,
//                         right: 0,
//                         bottom: 0,
//                         left: 10
//                     },
//                     xaxis: {
//                         lines: {
//                             show: true
//                         }
//                     },
//                     yaxis: {
//                         lines: {
//                             show: true
//                         }
//                     }
//                 },
//                 tooltip: {
//                     theme: theme.palette.mode,
//                     style: {
//                         fontSize: '14px'
//                     },
//                     y: {
//                         formatter: function (val: number) {
//                             return val + ' documents';
//                         },
//                         title: {
//                             formatter: function () {
//                                 return 'Count:';
//                             }
//                         }
//                     }
//                 },
//                 colors: [theme.palette.secondary.main],
//                 fill: {
//                     opacity: 0.9,
//                     type: 'solid'
//                 },
//                 stroke: {
//                     show: true,
//                     width: 2,
//                     colors: ['transparent']
//                 },
//                 legend: {
//                     show: false
//                 },
//                 responsive: [
//                     {
//                         breakpoint: 1920,
//                         options: {
//                             chart: {
//                                 height: '100%'
//                             },
//                             plotOptions: {
//                                 bar: {
//                                     columnWidth: '60%'
//                                 }
//                             },
//                             dataLabels: {
//                                 style: {
//                                     fontSize: '11px'
//                                 }
//                             },
//                             xaxis: {
//                                 labels: {
//                                     style: {
//                                         fontSize: '11px'
//                                     },
//                                     rotate: -45
//                                 }
//                             }
//                         }
//                     },
//                     {
//                         breakpoint: 1366,
//                         options: {
//                             chart: {
//                                 height: '100%'
//                             },
//                             plotOptions: {
//                                 bar: {
//                                     columnWidth: '65%'
//                                 }
//                             },
//                             dataLabels: {
//                                 style: {
//                                     fontSize: '10px'
//                                 }
//                             },
//                             xaxis: {
//                                 labels: {
//                                     style: {
//                                         fontSize: '10px'
//                                     },
//                                     rotate: -45
//                                 }
//                             }
//                         }
//                     },
//                     {
//                         breakpoint: 1024,
//                         options: {
//                             chart: {
//                                 height: '100%'
//                             },
//                             plotOptions: {
//                                 bar: {
//                                     columnWidth: '70%'
//                                 }
//                             },
//                             dataLabels: {
//                                 enabled: true,
//                                 style: {
//                                     fontSize: '9px'
//                                 }
//                             },
//                             xaxis: {
//                                 labels: {
//                                     style: {
//                                         fontSize: '9px'
//                                     },
//                                     rotate: -45
//                                 }
//                             }
//                         }
//                     },
//                     {
//                         breakpoint: 768,
//                         options: {
//                             chart: {
//                                 height: '100%'
//                             },
//                             plotOptions: {
//                                 bar: {
//                                     columnWidth: '75%',
//                                     borderRadius: 4
//                                 }
//                             },
//                             dataLabels: {
//                                 enabled: true,
//                                 style: {
//                                     fontSize: '8px'
//                                 },
//                                 offsetY: -15
//                             },
//                             xaxis: {
//                                 labels: {
//                                     style: {
//                                         fontSize: '8px'
//                                     },
//                                     rotate: -45,
//                                     maxHeight: 80
//                                 }
//                             },
//                             yaxis: {
//                                 labels: {
//                                     style: {
//                                         fontSize: '9px'
//                                     }
//                                 },
//                                 title: {
//                                     style: {
//                                         fontSize: '10px'
//                                     }
//                                 }
//                             }
//                         }
//                     },
//                     {
//                         breakpoint: 480,
//                         options: {
//                             chart: {
//                                 height: '100%'
//                             },
//                             plotOptions: {
//                                 bar: {
//                                     columnWidth: '80%',
//                                     borderRadius: 3
//                                 }
//                             },
//                             dataLabels: {
//                                 enabled: false // Hide data labels on very small screens
//                             },
//                             xaxis: {
//                                 labels: {
//                                     style: {
//                                         fontSize: '7px'
//                                     },
//                                     rotate: -45,
//                                     maxHeight: 60
//                                 }
//                             },
//                             yaxis: {
//                                 labels: {
//                                     style: {
//                                         fontSize: '8px'
//                                     }
//                                 },
//                                 title: {
//                                     style: {
//                                         fontSize: '9px'
//                                     }
//                                 }
//                             },
//                             grid: {
//                                 padding: {
//                                     left: 5,
//                                     right: 5
//                                 }
//                             }
//                         }
//                     }
//                 ]
//             });

//             setChartSeries([{
//                 name: 'Document Count',
//                 data: seriesData
//             }]);
//         } else {
//             setChartOptions(null);
//             setChartSeries([]);
//         }
//     }, [deptCountRawData, theme]);
//     useEffect(() => {
//         if (DepartmentList.length > 0) {
//             getTotalDoc();
//         }
//     }, [DepartmentList]);

//     // const getTotalDoc = async () => {
//     //     try {
//     //         const devDepartments = DepartmentList; // NewNodeMaster se aaya hua

//     //         // 🔥 ADMIN CONDITION
//     //         if (devDepartments.length > 1) {

//     //             const collectData = {
//     //                 type: 1,
//     //                 pageID: 1,
//     //                 searchBy: "",
//     //                 fromdt: "1900-01-01",
//     //                 todt: new Date().toISOString().slice(0, 10),
//     //                 print: 1
//     //             };

//     //             const res = await axios.post(
//     //                 HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`,
//     //                 collectData
//     //             );

//     //             const stats = res?.data?.data?.dashboardcls?.[0];

//     //             if (stats) {
//     //                 setTotalCount(stats.totDoc || 0);   // ✅ Admin ko full total
//     //                 setFileCount(stats.totType || 0);
//     //                 setIndexCount(stats.totIndexFile || 0);
//     //                 setSplitCount(stats.totsplitedFile || 0);
//     //             }

//     //         } else {

//     //             // 🔥 NORMAL USER CONDITION
//     //             const collectData = {
//     //                 type: 9,
//     //                 pageID: 1,
//     //                 searchBy: "",
//     //                 fromdt: "1900-01-01",
//     //                 todt: new Date().toISOString().slice(0, 10),
//     //                 print: 1
//     //             };

//     //             const res = await axios.post(
//     //                 HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`,
//     //                 collectData
//     //             );

//     //             const deptList = res?.data?.data?.departmentwiseCountRest;

//     //             if (Array.isArray(deptList) && devDepartments.length === 1) {

//     //                 const deptId = devDepartments[0].id;

//     //                 const matchedDept = deptList.find(
//     //                     (item: any) => item.divisionId === deptId
//     //                 );

//     //                 setTotalCount(matchedDept?.totalCount || 0);
//     //             }

//     //             // 👇 Baaki counts type:1 se hi lo
//     //             const res2 = await axios.post(
//     //                 HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`,
//     //                 {
//     //                     type: 1,
//     //                     pageID: 1,
//     //                     searchBy: "",
//     //                     fromdt: "1900-01-01",
//     //                     todt: new Date().toISOString().slice(0, 10),
//     //                     print: 1
//     //                 }
//     //             );

//     //             const stats = res2?.data?.data?.dashboardcls?.[0];

//     //             if (stats) {
//     //                 setFileCount(stats.totType || 0);
//     //                 setIndexCount(stats.totIndexFile || 0);
//     //                 setSplitCount(stats.totsplitedFile || 0);
//     //             }
//     //         }

//     //     } catch (error) {
//     //         console.error(error);
//     //     }
//     // };

//     const getIndexOrSplitData = async (type: number) => {

//         try {

//             // ⭐ RESET STATES
//             setIndexSplitPage(1);
//             setIndexSplitSearch("");
//             setIndexSplitData([]);

//             setIsIndexSplitModalOpen(true);
//             setIsIndexSplitLoading(true);

//             const res = await axios.post(
//                 HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`,
//                 {
//                     type: type,
//                     pageID: 1,
//                     searchBy: "",
//                     fromdt: "1900-01-01",
//                     todt: new Date().toISOString().slice(0, 10),
//                     print: 1
//                 }
//             );

//             let data: any[] = [];

//             if (type === 11) {

//                 setIndexSplitType("index");

//                 data = res?.data?.data?.departmentwiseIndexinglist || [];

//             }

//             if (type === 12) {

//                 setIndexSplitType("split");

//                 data = res?.data?.data?.departmentwiseIndexingsPendinglist || [];

//             }

//             const devDepartments = DepartmentList;
//             const devId = Number(getSecureDevId());

//             // Department user filter

//             if (devDepartments.length === 1) {

//                 data = data.filter((item: any) => item.deptID === devId);

//             }

//             setIndexSplitData(data);

//         } catch (error) {

//             console.error(error);

//         } finally {

//             setIsIndexSplitLoading(false);

//         }
//     };

//     const handleCloseIndexSplitModal = () => {

//         setIsIndexSplitModalOpen(false);

//         setIndexSplitPage(1);
//         setIndexSplitSearch("");
//         setIndexSplitData([]);

//     };
//     const filteredIndexSplitData = useMemo(() => {

//         if (!indexSplitSearch) return indexSplitData;

//         const q = indexSplitSearch.toLowerCase();

//         return indexSplitData.filter((item: any) =>
//             item.fileNo?.toLowerCase().includes(q) ||
//             item.fileDef?.toLowerCase().includes(q) ||
//             item.departmentName?.toLowerCase().includes(q)
//         );

//     }, [indexSplitData, indexSplitSearch]);
//     const getTotalDoc = async () => {
//         try {

//             const devDepartments = DepartmentList;

//             // ================= ADMIN =================
//             if (devDepartments.length > 1) {

//                 const res = await axios.post(
//                     HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`,
//                     {
//                         type: 1,
//                         pageID: 1,
//                         searchBy: "",
//                         fromdt: "1900-01-01",
//                         todt: new Date().toISOString().slice(0, 10),
//                         print: 1
//                     }
//                 );

//                 const stats = res?.data?.data?.dashboardcls?.[0];

//                 if (stats) {
//                     setTotalCount(stats.totDoc || 0);
//                     setFileCount(stats.totType || 0);

//                     // ✅ Admin Index
//                     setIndexCount(stats.totIndexFile || 0);

//                     // ✅ Admin Split
//                     setSplitCount(stats.pending || 0);
//                 }

//             }

//             // ================= DEPARTMENT USER =================
//             else {

//                 const res = await axios.post(
//                     HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`,
//                     {
//                         type: 9,
//                         pageID: 1,
//                         searchBy: "",
//                         fromdt: "1900-01-01",
//                         todt: new Date().toISOString().slice(0, 10),
//                         print: 1
//                     }
//                 );

//                 const deptList = res?.data?.data?.departmentwiseCountRest;

//                 if (Array.isArray(deptList)) {

//                     const devId = Number(getSecureDevId());

//                     const matchedDept = deptList.find(
//                         (item: any) => item.divisionId === devId
//                     );

//                     if (matchedDept) {

//                         // ✅ Total Document
//                         setTotalCount(matchedDept.totalCount || 0);

//                         // ✅ Index File
//                         setIndexCount(matchedDept.totalIndexed || 0);

//                         // ✅ Split File
//                         setSplitCount(matchedDept.remainingIndex || 0);
//                     }

//                 }

//                 // ===== File Type =====

//                 const res2 = await axios.post(
//                     HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`,
//                     {
//                         type: 1,
//                         pageID: 1,
//                         searchBy: "",
//                         fromdt: "1900-01-01",
//                         todt: new Date().toISOString().slice(0, 10),
//                         print: 1
//                     }
//                 );

//                 const stats = res2?.data?.data?.dashboardcls?.[0];

//                 if (stats) {
//                     setFileCount(stats.totType || 0);
//                 }

//             }

//         } catch (error) {
//             console.error(error);
//         }
//     };

//     const getDepartmentTotalDocument = async () => {
//         try {
//             const collectData = {
//                 type: 9,
//                 pageID: 1,
//                 searchBy: "",
//                 fromdt: "1900-01-01",
//                 todt: new Date().toISOString().slice(0, 10),
//                 print: 1
//             };

//             const res = await axios.post(
//                 HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`,
//                 collectData
//             );

//             const deptList = res?.data?.data?.departmentwiseCountRest;

//             if (Array.isArray(deptList)) {

//                 const devId = Number(getSecureDevId());

//                 const matchedDept = deptList.find(
//                     (item: any) => item.divisionId === devId
//                 );

//                 if (matchedDept) {
//                     setTotalCount(matchedDept.totalCount || 0);
//                 } else {
//                     setTotalCount(0);
//                 }
//             }

//         } catch (error) {
//             console.error(error);
//             setTotalCount(0);
//         }
//     };

//     const getTotalFilesDataAndShowModal = () => {
//         setIsLoadingTotalFiles(true);
//         setIndex([]); // Reset state
//         setTotalFilesSearchQuery("");
//         setTotalFilesPage(1); // Reset to first page
//         setIsTotalFilesModalOpen(true);
//         // Adjusted type based on likely API expectation (check your API docs)
//         const collectData = { type: 10, pageID: 1, searchBy: "", fromdt: "1900-01-01", todt: new Date().toISOString().slice(0, 10), print: 1 };
//         axios.post(HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`, collectData)
//             .then((res) => {
//                 const data = res?.data?.data?.totalIndex; // Check path based on actual API response
//                 setIndex(Array.isArray(data) ? data : []);
//             })
//             .catch(error => {
//                 console.error("Error fetching total index/split files:", error);
//                 setIndex([]); // Ensure state is array on error
//             })
//             .finally(() => setIsLoadingTotalFiles(false));
//     };


//     const getDepartmentList = () => {
//         try {
//             // Use secureGetItem instead of localStorage.getItem
//             const devIdData = secureGetItem("devID");
//             const devId = devIdData !== null && devIdData !== "null" ? devIdData : -1;

//             const collectData = {
//                 id: typeof devId === 'number' ? devId : -1,
//                 nodeID: -1,
//                 titleID: -1
//             };

//             axios.post(HOST_URL.HOST_URL2 + `NewNodeMaster/GetNewNodeMaster`, collectData)
//                 .then((res) => {
//                     const departments: any[] = res?.data?.data || [];
//                     setDepartmentList(departments);
//                     setFilteredDepartmentList(departments); // Initialize filtered list
//                 })
//                 .catch(error => console.error("Error fetching department list:", error));
//         } catch (error) {
//             console.error("Error getting devID from secure storage:", error);

//             // Fallback: try regular localStorage
//             try {
//                 const devId = localStorage.getItem("devID");
//                 const collectData = {
//                     id: devId === null || devId === "null" ? -1 : Number(devId),
//                     nodeID: -1,
//                     titleID: -1
//                 };

//                 axios.post(HOST_URL.HOST_URL2 + `NewNodeMaster/GetNewNodeMaster`, collectData)
//                     .then((res) => {
//                         const departments: any[] = res?.data?.data || [];
//                         setDepartmentList(departments);
//                         setFilteredDepartmentList(departments);
//                     })
//                     .catch(fallbackError => console.error("Error in fallback department fetch:", fallbackError));
//             } catch (fallbackError) {
//                 console.error("Fallback also failed:", fallbackError);
//                 setDepartmentList([]);
//                 setFilteredDepartmentList([]);
//             }
//         }
//     };

//     const getDepartmentCountChartData = () => {
//         setIsDeptChartLoading(true);
//         setDeptChartError(null);
//         // Adjusted type based on likely API expectation (check your API docs)
//         const collectData = { type: 9, pageID: 1, searchBy: "", fromdt: "1900-01-01", todt: new Date().toISOString().slice(0, 10), print: 1 };
//         axios.post(HOST_URL.HOST_URL2 + `DashboardEntity/GetDashboardData`, collectData)
//             .then((res) => {
//                 const data = res?.data?.data?.departmentwiseCountRest; // Check path based on actual API response
//                 if (data && Array.isArray(data)) {
//                     setDeptCountRawData(data);
//                 } else {
//                     console.warn("API did not return expected department count data format:", res?.data);
//                     setDeptCountRawData([]);
//                     setDeptChartError("Invalid data format received for department chart.");
//                 }
//             })
//             .catch(error => {
//                 console.error("Error fetching department count chart:", error);
//                 setDeptChartError("Failed to load department chart data.");
//                 setDeptCountRawData([]);
//             })
//             .finally(() => setIsDeptChartLoading(false));
//     };

//     const devId = getSecureDevId();
//     const getDocEntryAndShowModal = () => {
//         setIsLoadingModal(true);
//         setModalError(null);
//         setModalData([]);
//         setOpenRowId(null);
//         setSearchQuery("");
//         setModalPage(1); // Reset to first page
//         setIsModalOpen(true);
//         const collectData = {
//             docMid: -1,
//             fileTypId: -1,
//             //@ts-ignore
//             divisionid: devId === "null" ? -1 : devId,
//             subsubjId: -1,
//             user_Id: -1,
//             fileNo: "",
//             subDeptID: -1,
//             complt: ""
//         };
//         axios.post(HOST_URL.HOST_URL2 + `DocMangr/GetDocMangr`, collectData)
//             .then((res) => {
//                 if (res?.data?.isSuccess && Array.isArray(res.data.data)) {
//                     setModalData(res.data.data);
//                 } else {
//                     setModalError(res?.data?.mesg || "Failed to fetch data or invalid format received.");
//                     setModalData([]);
//                 }
//             })
//             .catch((error) => {
//                 console.error("Error fetching document entry data:", error);
//                 setModalError("An error occurred while fetching document details.");
//                 setModalData([]);
//             })
//             .finally(() => setIsLoadingModal(false));
//     };

//     const getDocTypeAndShowModal = () => {
//         setIsLoadingDocTypeModal(true);
//         setDocTypeModalError(null);
//         setDocTypeData([]);
//         setDocTypeSearchQuery("");
//         setDocTypePage(1); // Reset to first page
//         setIsDocTypeModalOpen(true);
//         // Ensure userId is handled appropriately (e.g., from context/auth or default)
//         const collectData = { docID: -1, parentDocId: 0, userId: "" }; // Consider passing actual userId if needed
//         axios.post(HOST_URL.HOST_URL2 + `OtherDocType/GetOtherDocType`, collectData)
//             .then((res) => {
//                 if (res?.data?.isSuccess && Array.isArray(res.data.data)) {
//                     setDocTypeData(res.data.data);
//                 } else {
//                     setDocTypeModalError(res?.data?.mesg || "Failed to fetch document types or invalid format received.");
//                     setDocTypeData([]);
//                 }
//             })
//             .catch((error) => {
//                 console.error("Error fetching document type data:", error);
//                 setDocTypeModalError("An error occurred while fetching document types.");
//                 setDocTypeData([]);
//             })
//             .finally(() => setIsLoadingDocTypeModal(false));
//     };


//     // --- Event Handlers ---
//     const handleDepartmentClick = async (dept: any) => {
//         const deptId = dept?.id;
//         const deptName = dept?.name;
//         if (!deptId || !deptName) return;

//         setSelectedDepartment(deptName);
//         setSelectedDepartmentId(deptId);
//         setIsLoadingDeptData(true);
//         setDeptDataError(null);
//         setSelectedDeptData([]);
//         setDeptDataSearchQuery("");
//         setOpenDrawerRowId(null);
//         setDrawerPage(1); // Reset to first page
//         setIsDeptDrawerOpen(true); // Open the drawer

//         const collectData = {
//             docMid: -1,
//             fileTypId: -1,
//             divisionid: deptId,
//             subsubjId: -1,
//             user_Id: -1,
//             fileNo: "",
//             subDeptID: -1,
//             complt: "",
//         };

//         try {
//             const resp = await axios.post(HOST_URL.HOST_URL2 + `DocMangr/GetDocMangr`, collectData);
//             if (resp?.data?.isSuccess && Array.isArray(resp.data.data)) {
//                 setSelectedDeptData(resp.data.data);
//             } else {
//                 console.warn("Department data fetch warning:", resp?.data?.mesg);
//                 setDeptDataError(resp?.data?.mesg || `No documents found for ${deptName}.`);
//                 setSelectedDeptData([]);
//             }
//         } catch (error) {
//             console.error(`Error fetching data for department ${deptName} (ID: ${deptId}):`, error);
//             setDeptDataError(`An error occurred while fetching documents for ${deptName}.`);
//             setSelectedDeptData([]);
//         } finally {
//             setIsLoadingDeptData(false);
//         }
//     };

//     const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
//         if (event && event.type === 'keydown' &&
//             ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) {
//             return;
//         }
//         setIsDeptDrawerOpen(open);
//         if (!open) {
//             setSelectedDepartment(null);
//             setSelectedDepartmentId(null);
//             setDeptDataSearchQuery("");
//             setOpenDrawerRowId(null);
//             setDrawerPage(1); // Reset pagination when closing
//         }
//     };

//     const handleCloseModal = () => {
//         setIsModalOpen(false);
//         setSearchQuery("");
//         setOpenRowId(null);
//         setModalPage(1); // Reset pagination
//     };

//     const handleCloseDocTypeModal = () => {
//         setIsDocTypeModalOpen(false);
//         setDocTypeSearchQuery("");
//         setDocTypePage(1); // Reset pagination
//     };

//     const handleCloseTotalFilesModal = () => {
//         setIsTotalFilesModalOpen(false);
//         setTotalFilesSearchQuery("");
//         setTotalFilesPage(1); // Reset pagination
//     };

//     // --- Filtered Data ---
//     const filteredModalData = useMemo(() => {
//         if (!searchQuery) return modalData;
//         const lowerCaseQuery = searchQuery.toLowerCase();
//         return modalData.filter(doc =>
//             (doc.fileNo?.toLowerCase().includes(lowerCaseQuery)) ||
//             (doc.fileDef?.toLowerCase().includes(lowerCaseQuery)) ||
//             (doc.department_Name?.toLowerCase().includes(lowerCaseQuery)) ||
//             (doc.subj?.toLowerCase().includes(lowerCaseQuery)) ||
//             (String(doc.docMid)?.toLowerCase().includes(lowerCaseQuery))
//         );
//     }, [modalData, searchQuery]);

//     const filteredDocTypeData = useMemo(() => {
//         if (!docTypeSearchQuery) return docTypeData;
//         const lowerCaseQuery = docTypeSearchQuery.toLowerCase();
//         return docTypeData.filter(docType =>
//             (docType.doc_Name?.toLowerCase().includes(lowerCaseQuery)) ||
//             (docType.doc_shortname?.toLowerCase().includes(lowerCaseQuery)) ||
//             (docType.parentDoc_Name?.toLowerCase().includes(lowerCaseQuery)) ||
//             (docType.session_year?.toLowerCase().includes(lowerCaseQuery))
//         );
//     }, [docTypeData, docTypeSearchQuery]);

//     const filteredTotalFilesData = useMemo(() => {
//         if (!totalFilesSearchQuery || !Array.isArray(isIndex)) return isIndex || []; // Ensure return array
//         const lowerCaseQuery = totalFilesSearchQuery.toLowerCase();
//         // Add optional chaining for safety
//         return (isIndex || []).filter(item =>
//             item?.fileName?.toLowerCase().includes(lowerCaseQuery)
//         );
//     }, [isIndex, totalFilesSearchQuery]);

//     const filteredSelectedDeptData = useMemo(() => {
//         if (!deptDataSearchQuery) return selectedDeptData;
//         const lowerCaseQuery = deptDataSearchQuery.toLowerCase();
//         return selectedDeptData.filter(doc =>
//             (doc.fileNo?.toLowerCase().includes(lowerCaseQuery)) ||
//             (doc.fileDef?.toLowerCase().includes(lowerCaseQuery)) ||
//             (doc.subj?.toLowerCase().includes(lowerCaseQuery)) ||
//             (String(doc.docMid)?.toLowerCase().includes(lowerCaseQuery))
//         );
//     }, [selectedDeptData, deptDataSearchQuery]);

//     // --- Chart Options ---
//     const optionsDeptCountChart = {
//         chartArea: { width: '85%', height: '70%' },
//         hAxis: { title: 'Department', textStyle: { fontSize: 10, slantedText: true, slantedTextAngle: 35 } },
//         vAxis: {
//             title: 'Total Count',
//             minValue: 0,
//             textStyle: { fontSize: 10 },
//             gridlines: { color: '#eee' }
//         },
//         legend: { position: 'none' } as const,
//         bar: { groupWidth: '75%' },
//         animation: { startup: true, duration: 1200, easing: 'out' as const },
//         tooltip: { isHtml: true },
//         colors: [theme.palette.secondary.main],
//         annotations: {
//             textStyle: {
//                 fontSize: 11,
//                 bold: false,
//                 color: '#333333',
//                 auraColor: 'none'
//             },
//             alwaysOutside: true,
//             stem: {
//                 color: 'transparent',
//                 length: 0
//             }
//         }
//     };

//     // --- Styles ---
//     const cardHoverStyle = {
//         transition: theme.transitions.create(['transform', 'box-shadow', 'background-color'], {
//             duration: theme.transitions.duration.short, // Use theme transition duration
//         }),
//         '&:hover': {
//             transform: 'scale(1.03)', // Slightly larger pop
//             boxShadow: theme.shadows[10], // More pronounced shadow
//             // Optional: subtle background tint on hover
//             // backgroundColor: alpha(theme.palette.primary.light, 0.05),
//         },
//     };

//     // --- Stat Items Array (with icons and colors) ---
//     const statItems = [
//         {
//             // title: "Total Document",
//             title: t("text.totalDoc"),
//             count: TotalCount,
//             icon: DescriptionOutlinedIcon,
//             color: theme.palette.primary.main,
//             contrastColor: theme.palette.primary.contrastText,
//             onClick: getDocEntryAndShowModal
//         },
//         {
//             // title: "Total Type",
//             title: t("text.totalType"),
//             count: FileCount,
//             icon: CategoryOutlinedIcon,
//             color: theme.palette.secondary.main,
//             contrastColor: theme.palette.secondary.contrastText,
//             onClick: getDocTypeAndShowModal
//         },
//         {
//             // title: "Total Index File",
//             title: t("text.totalIndexFile"),
//             count: IndexCount,
//             icon: FolderZipOutlinedIcon,
//             color: theme.palette.success.dark,
//             contrastColor: theme.palette.success.contrastText,
//             onClick: () => getIndexOrSplitData(11)
//         },

//         {
//             // title: "Pending Indexing",
//             title: t("text.pendingIndexing"),
//             count: SplitCount,
//             icon: CallSplitOutlinedIcon,
//             color: theme.palette.info.main,
//             contrastColor: theme.palette.info.contrastText,
//             onClick: () => getIndexOrSplitData(12)
//         }
//     ];


//     // --- Render ---
//     return (
//         <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)' /* Adjust if needed */ }}>

//             {/* Main Content Area */}
//             <Box
//                 component="main"
//                 sx={{
//                     flexGrow: 1,
//                     p: { xs: 1.5, sm: 2, md: 3 },
//                     backgroundColor: theme.palette.background.default // Use theme background
//                 }}
//             >
//                 <Grid container spacing={{ xs: 2, md: 3 }} >

//                     {/* === REVISED Stat Cards Section === */}
//                     {statItems.map((item: any, index) => (
//                         <Grid item xs={12} sm={6} md={3} key={index}>
//                             <Card
//                                 elevation={5} // Slightly increased elevation
//                                 sx={{
//                                     borderRadius: '16px', // More rounded corners
//                                     minHeight: '100px', // Adjusted height
//                                     display: 'flex', // Use flex for content alignment
//                                     overflow: 'hidden', // Prevent content spillover
//                                     cursor: item.onClick ? 'pointer' : 'default',
//                                     backgroundColor: theme.palette.background.paper, // Use paper background
//                                     ...cardHoverStyle, // Apply hover style
//                                 }}
//                                 onClick={item.onClick}
//                             >
//                                 <CardContent sx={{
//                                     display: 'flex',
//                                     alignItems: 'center', // Vertically center content
//                                     justifyContent: 'space-between', // Space out icon and text
//                                     p: 2.5, // Adjust padding
//                                     width: '100%', // Ensure content takes full width
//                                     gap: 2, // Space between icon avatar and text box
//                                 }}>
//                                     {/* Icon Side */}
//                                     <Avatar
//                                         sx={{
//                                             bgcolor: item.color,
//                                             color: item.contrastColor,
//                                             width: 60, // Larger avatar/icon background
//                                             height: 60,
//                                             boxShadow: `0 4px 12px -2px ${item.color}aa` // Soft shadow matching the color
//                                         }}
//                                     >
//                                         <item.icon sx={{ fontSize: '2.2rem' }} /> {/* Larger Icon */}
//                                     </Avatar>

//                                     {/* Text Side */}
//                                     <Box sx={{
//                                         display: 'flex',
//                                         flexDirection: 'column',
//                                         alignItems: 'flex-end', // Align text to the right
//                                         textAlign: 'right',
//                                         overflow: 'hidden', // Prevent text overflow issues
//                                     }}>
//                                         <Typography
//                                             variant="body2" // Smaller, descriptive title
//                                             color="text.secondary"
//                                             sx={{ fontWeight: 500, mb: 0.5 }}
//                                             noWrap // Prevent title wrapping
//                                         >
//                                             {item.title}
//                                         </Typography>
//                                         <Typography
//                                             variant="h4" // Large, prominent count
//                                             fontWeight="bold"
//                                             color="text.primary"
//                                             sx={{ lineHeight: 1.2 }} // Adjust line height
//                                         >
//                                             <CountUp end={item.count} duration={1.5} separator="," />
//                                         </Typography>
//                                     </Box>
//                                 </CardContent>
//                             </Card>
//                         </Grid>
//                     ))}


//                     {/* Department List Card */}
//                     <Grid item xs={12} md={4}>
//                         <Card elevation={3} sx={{ borderRadius: '12px', height: '400px', display: 'flex', flexDirection: 'column' }}>
//                             <CardHeader
//                                 title={t("text.selectDepartment")}
//                                 titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
//                                 sx={{
//                                     background: `linear-gradient(135deg, ${theme.palette.warning.light} 0%, ${theme.palette.warning.main} 100%)`,
//                                     color: theme.palette.warning.contrastText,
//                                     py: 1.5,
//                                     flexShrink: 0
//                                 }}
//                             />

//                             {/* Search Field */}
//                             <Box sx={{ p: 1.5, pb: 0, flexShrink: 0 }}>
//                                 <TextField
//                                     fullWidth
//                                     size="small"
//                                     variant="outlined"
//                                     placeholder={t("text.searchDepartment")}
//                                     onChange={(e) => {
//                                         const searchTerm = e.target.value.toLowerCase();
//                                         if (searchTerm === '') {
//                                             setFilteredDepartmentList(DepartmentList);
//                                         } else {
//                                             const filtered = DepartmentList.filter(dept =>
//                                                 dept.name.toLowerCase().includes(searchTerm)
//                                             );
//                                             setFilteredDepartmentList(filtered);
//                                         }
//                                     }}
//                                     sx={{
//                                         backgroundColor: 'white',
//                                         '& .MuiOutlinedInput-root': {
//                                             borderRadius: '8px'
//                                         }
//                                     }}
//                                 />
//                             </Box>

//                             <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1, pt: 0 }}>
//                                 <List component="nav" dense>
//                                     {filteredDepartmentList.length > 0 ? (
//                                         filteredDepartmentList.map((department) => (
//                                             <ListItemButton
//                                                 key={department.id}
//                                                 selected={selectedDepartmentId === department.id}
//                                                 onClick={() => handleDepartmentClick(department)}
//                                                 sx={{
//                                                     borderRadius: '8px',
//                                                     my: 0.5,
//                                                     '&.Mui-selected': {
//                                                         backgroundColor: theme.palette.primary.main,
//                                                         color: theme.palette.primary.contrastText,
//                                                         '&:hover': { backgroundColor: theme.palette.primary.dark }
//                                                     },
//                                                     '&:hover': { backgroundColor: theme.palette.action.hover },
//                                                     justifyContent: "center",
//                                                 }}
//                                             >
//                                                 <ListItemText
//                                                     primary={department.name}
//                                                     primaryTypographyProps={{
//                                                         fontWeight: 500,
//                                                         textAlign: 'center',
//                                                         sx: {
//                                                             overflow: 'hidden',
//                                                             textOverflow: 'ellipsis',
//                                                             whiteSpace: 'nowrap'
//                                                         }
//                                                     }}
//                                                 />
//                                             </ListItemButton>
//                                         ))
//                                     ) : (
//                                         <ListItem>
//                                             <ListItemText
//                                                 primary={t("text.noDepartmentFound")}
//                                                 sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}
//                                             />
//                                         </ListItem>
//                                     )}
//                                 </List>
//                             </Box>
//                         </Card>
//                     </Grid>

//                     {/* Department Wise Count Chart */}
//                     <Grid item xs={12} md={8}>
//                         <Card
//                             elevation={3}
//                             sx={{
//                                 borderRadius: '16px',
//                                 height: '400px',   // ✅ Match Department List height
//                                 display: 'flex',
//                                 flexDirection: 'column',
//                                 width: '100%'
//                             }}
//                         >
//                             <CardHeader
//                                 // title="Department Wise Document Counts"
//                                 title={t("text.departmentWiseDocCount")}
//                                 titleTypographyProps={{
//                                     variant: 'h6',
//                                     fontWeight: 'bold'
//                                 }}
//                                 sx={{
//                                     background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
//                                     color: theme.palette.secondary.contrastText,
//                                     py: 1.5,
//                                     flexShrink: 0
//                                 }}
//                             />
//                             <CardContent
//                                 sx={{
//                                     flexGrow: 1,
//                                     p: 1.5,
//                                     display: 'flex',
//                                     justifyContent: 'center',
//                                     alignItems: 'center',
//                                     overflow: 'hidden',
//                                     width: '100%'
//                                 }}
//                             >
//                                 {isDeptChartLoading ? (
//                                     <Box sx={{
//                                         display: 'flex',
//                                         flexDirection: 'column',
//                                         alignItems: 'center',
//                                         gap: 2,
//                                         width: '100%',
//                                         height: '100%',
//                                         justifyContent: 'center'
//                                     }}>
//                                         <CircularProgress color="secondary" size={40} />
//                                         <Typography variant="body2" color="text.secondary">
//                                             Loading chart data...
//                                         </Typography>
//                                     </Box>
//                                 ) : deptChartError ? (
//                                     <Alert severity="error" sx={{ width: '90%', m: 1 }}>
//                                         {deptChartError}
//                                     </Alert>
//                                 ) : chartOptions && chartSeries.length > 0 ? (
//                                     <Box
//                                         sx={{
//                                             width: '100%',
//                                             height: '100%',
//                                             position: 'relative'
//                                         }}
//                                     >
//                                         <ReactApexChart
//                                             options={chartOptions}
//                                             series={chartSeries}
//                                             type="bar"
//                                             height="100%"
//                                             width="100%"
//                                         />
//                                     </Box>
//                                 ) : (
//                                     <Box
//                                         sx={{
//                                             display: 'flex',
//                                             alignItems: 'center',
//                                             justifyContent: 'center',
//                                             width: '100%',
//                                             height: '100%'
//                                         }}
//                                     >
//                                         <Typography
//                                             variant="body1"
//                                             sx={{ textAlign: 'center', color: 'text.secondary', p: 2 }}
//                                         >
//                                             No department count data available.
//                                         </Typography>
//                                     </Box>
//                                 )}
//                             </CardContent>
//                         </Card>
//                     </Grid>

//                 </Grid>
//             </Box> {/* End Main Content Area */}

//             {/* --- Document Details Modal --- */}
//             <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="xl" fullWidth scroll="paper" PaperProps={{ sx: { maxHeight: '90vh', height: 'calc(100% - 64px)' } }}>
//                 <DialogTitle sx={{ bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, py: 1.5, fontSize: '1.1rem', m: 0, borderBottom: `1px solid ${theme.palette.divider}` }}>
//                    {t("text.documentDetails")}
//                 </DialogTitle>
//                 <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.grey[50], position: 'sticky', top: 0, zIndex: 1101 }}>
//                     <TextField fullWidth size="small" variant="outlined" label={t("text.searchDocuments")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ backgroundColor: 'white' }} />
//                 </Box>
//                 {isLoadingModal && <LinearProgress color="primary" sx={{ position: 'sticky', top: 57, zIndex: 1101 }} />}
//                 <DialogContent dividers sx={{ p: 0, '&.MuiDialogContent-root': { padding: 0 } }}>
//                     {modalError ? (
//                         <Box sx={{ p: 3 }}><Alert severity="error">{modalError}</Alert></Box>
//                     ) : (
//                         <>
//                             <TableContainer component={Paper} elevation={0} square sx={{ maxHeight: 'calc(100vh - 64px - 58px - 53px)' /* Adjust */ }}>
//                                 <Table stickyHeader aria-label="collapsible document table" size="small">
//                                     <TableHead>
//                                         <TableRow sx={{ '& th': { backgroundColor: theme.palette.grey[200], fontWeight: 'bold', color: theme.palette.text.primary, padding: '6px 10px', position: 'sticky', top: 0, zIndex: 1 } }}>
//                                             <TableCell sx={{ width: '50px' }} />
//                                             {/* <TableCell>File No</TableCell>
//                                             <TableCell>File Name</TableCell>
//                                             <TableCell>Department</TableCell>
//                                             {/* <TableCell>Subject</TableCell> */}
//                                             {/* <TableCell>Date</TableCell>
//                                             <TableCell>Files</TableCell>
//                                             <TableCell align="center">Preview</TableCell> */}
//                                             <TableCell>{t("text.fileNo")}</TableCell>
//                                             <TableCell>{t("text.fileName")}</TableCell>
//                                             <TableCell>{t("text.department")}</TableCell>
//                                             <TableCell>{t("text.date")}</TableCell>
//                                             <TableCell>{t("text.files")}</TableCell>
//                                             <TableCell>{t("text.preview")}</TableCell>
//                                         </TableRow>
//                                     </TableHead>
//                                     <TableBody>
//                                         {isLoadingModal && !modalData.length ? (
//                                             <TableRow><TableCell colSpan={7} align="center" sx={{ p: 5 }}><CircularProgress /></TableCell></TableRow>
//                                         ) : filteredModalData.length > 0 ? (
//                                             filteredModalData
//                                                 .slice((modalPage - 1) * modalRowsPerPage, modalPage * modalRowsPerPage)
//                                                 .map((doc, index) => (
//                                                     <DocRow
//                                                         key={doc.docMid}
//                                                         doc={doc}
//                                                         isOpen={openRowId === doc.docMid}
//                                                         onToggle={() => setOpenRowId(openRowId === doc.docMid ? null : doc.docMid)}
//                                                         formatDate={formatDate}
//                                                         theme={theme}
//                                                         isEvenRow={index % 2 === 0}
//                                                     />
//                                                 ))
//                                         ) : (
//                                             <TableRow><TableCell colSpan={7} align="center"><Typography color="textSecondary" sx={{ p: 3, fontStyle: 'italic' }}>{searchQuery ? "No documents match your search criteria." : "No document details found."}</Typography></TableCell></TableRow>
//                                         )}
//                                     </TableBody>
//                                 </Table>
//                             </TableContainer>

//                             {/* Pagination for Document Modal */}
//                             <Box
//                                 sx={{
//                                     display: "flex",
//                                     justifyContent: "space-between",
//                                     alignItems: "center",
//                                     py: 2,
//                                     px: 2,
//                                     borderTop: `1px solid ${theme.palette.divider}`,
//                                     backgroundColor: theme.palette.background.paper
//                                 }}
//                             >
//                                 <Typography variant="body2" fontWeight="bold">
//                                     {t("text.totalRecords")}: {filteredModalData.length}
//                                 </Typography>

//                                 <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                                     <IconButton
//                                         color="primary"
//                                         disabled={modalPage === 1}
//                                         onClick={() => setModalPage(prev => prev - 1)}
//                                     >
//                                         <ChevronLeftIcon />
//                                     </IconButton>

//                                     <Typography variant="body2" sx={{ fontWeight: 600 }}>
//                                         {modalPage} / {Math.ceil(filteredModalData.length / modalRowsPerPage) || 1}
//                                     </Typography>

//                                     <IconButton
//                                         color="primary"
//                                         disabled={modalPage >= Math.ceil(filteredModalData.length / modalRowsPerPage)}
//                                         onClick={() => setModalPage(prev => prev + 1)}
//                                     >
//                                         <ChevronRightIcon />
//                                     </IconButton>
//                                 </Box>
//                             </Box>
//                         </>
//                     )}
//                 </DialogContent>
//                 <DialogActions sx={{ p: 1.5, borderTop: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.grey[50] }}>
//                     <Button onClick={handleCloseModal} variant="contained" color="primary" size="medium">{t("text.close")}</Button>
//                 </DialogActions>
//             </Dialog>

//             {/* --- Document Type Modal --- */}
//             <Dialog open={isDocTypeModalOpen} onClose={handleCloseDocTypeModal} maxWidth="lg" fullWidth scroll="paper" PaperProps={{ sx: { maxHeight: '85vh', height: 'calc(100% - 64px)' } }}>
//                 <DialogTitle sx={{ bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, py: 1.5, fontSize: '1.1rem', m: 0, borderBottom: `1px solid ${theme.palette.divider}` }}>
//                    {t("text.documentTypes")}
//                 </DialogTitle>
//                 <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.grey[50], position: 'sticky', top: 0, zIndex: 1101 }}>
//                     <TextField fullWidth size="small" variant="outlined" label={t("text.searchDocTypes")} value={docTypeSearchQuery} onChange={(e) => setDocTypeSearchQuery(e.target.value)} sx={{ backgroundColor: 'white' }} />
//                 </Box>
//                 {isLoadingDocTypeModal && <LinearProgress color="secondary" sx={{ position: 'sticky', top: 57, zIndex: 1101 }} />}
//                 <DialogContent dividers sx={{ p: 0, '&.MuiDialogContent-root': { padding: 0 } }}>
//                     {docTypeModalError ? (
//                         <Box sx={{ p: 3 }}><Alert severity="error">{docTypeModalError}</Alert></Box>
//                     ) : (
//                         <>
//                             <TableContainer component={Paper} elevation={0} square sx={{ maxHeight: 'calc(100vh - 64px - 58px - 53px)' /* Adjust */ }}>
//                                 <Table stickyHeader aria-label="document type table" size="small">
//                                     <TableHead>
//                                         <TableRow sx={{ '& th': { backgroundColor: theme.palette.grey[200], fontWeight: 'bold', color: theme.palette.text.primary, padding: '6px 10px', position: 'sticky', top: 0, zIndex: 1 } }}>
//                                             {/* <TableCell>Name</TableCell>
//                                             <TableCell>Short Name</TableCell>
//                                             <TableCell>Parent Name</TableCell>
//                                             <TableCell align="right">Entry Date</TableCell> */}
//                                             <TableCell>{t("text.name")}</TableCell>
//                                             <TableCell>{t("text.shortName")}</TableCell>
//                                             <TableCell>{t("text.parentName")}</TableCell>
//                                             <TableCell align="right">{t("text.entryDate")}</TableCell>
//                                         </TableRow>
//                                     </TableHead>
//                                     <TableBody>
//                                         {isLoadingDocTypeModal && !docTypeData.length ? (
//                                             <TableRow><TableCell colSpan={4} align="center" sx={{ p: 5 }}><CircularProgress color="secondary" /></TableCell></TableRow>
//                                         ) : filteredDocTypeData.length > 0 ? (
//                                             filteredDocTypeData
//                                                 .slice((docTypePage - 1) * docTypeRowsPerPage, docTypePage * docTypeRowsPerPage)
//                                                 .map((docType, index) => (
//                                                     <TableRow key={docType.docID || `doctype-${index}`} sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover }, '&:last-child td, &:last-child th': { border: 0 }, }}>
//                                                         <TableCell sx={{ padding: '6px 10px' }}>{docType.doc_Name || '-'}</TableCell>
//                                                         <TableCell sx={{ padding: '6px 10px' }}>{docType.doc_shortname || '-'}</TableCell>
//                                                         <TableCell sx={{ padding: '6px 10px' }}>{docType.parentDoc_Name || '-'}</TableCell>
//                                                         <TableCell align="right" sx={{ padding: '6px 10px' }}>{formatDate(docType.entry_Date)}</TableCell>
//                                                     </TableRow>
//                                                 ))
//                                         ) : (
//                                             <TableRow><TableCell colSpan={4} align="center"><Typography color="textSecondary" sx={{ p: 3, fontStyle: 'italic' }}>{docTypeSearchQuery ? "No document types match your search criteria." : "No document types found."}</Typography></TableCell></TableRow>
//                                         )}
//                                     </TableBody>
//                                 </Table>
//                             </TableContainer>

//                             {/* Pagination for Document Type Modal */}
//                             <Box
//                                 sx={{
//                                     display: "flex",
//                                     justifyContent: "space-between",
//                                     alignItems: "center",
//                                     py: 2,
//                                     px: 2,
//                                     borderTop: `1px solid ${theme.palette.divider}`,
//                                     backgroundColor: theme.palette.background.paper
//                                 }}
//                             >
//                                 <Typography variant="body2" fontWeight="bold">
//                                     {t("text.totalRecords")}: {filteredDocTypeData.length}
//                                 </Typography>

//                                 <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                                     <IconButton
//                                         color="primary"
//                                         disabled={docTypePage === 1}
//                                         onClick={() => setDocTypePage(prev => prev - 1)}
//                                     >
//                                         <ChevronLeftIcon />
//                                     </IconButton>

//                                     <Typography variant="body2" sx={{ fontWeight: 600 }}>
//                                         {docTypePage} / {Math.ceil(filteredDocTypeData.length / docTypeRowsPerPage) || 1}
//                                     </Typography>

//                                     <IconButton
//                                         color="primary"
//                                         disabled={docTypePage >= Math.ceil(filteredDocTypeData.length / docTypeRowsPerPage)}
//                                         onClick={() => setDocTypePage(prev => prev + 1)}
//                                     >
//                                         <ChevronRightIcon />
//                                     </IconButton>
//                                 </Box>
//                             </Box>
//                         </>
//                     )}
//                 </DialogContent>
//                 <DialogActions sx={{ p: 1.5, borderTop: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.grey[50] }}>
//                     <Button onClick={handleCloseDocTypeModal} variant="contained" color="primary" size="medium">{t("text.close")}</Button>
//                 </DialogActions>
//             </Dialog>

//             {/* --- Total Index/Split Files Modal --- */}

//             <Dialog
//                 open={isIndexSplitModalOpen}
//                 onClose={handleCloseIndexSplitModal}
//                 maxWidth="xl"
//                 fullWidth
//                 scroll="paper"
//                 PaperProps={{
//                     sx: { maxHeight: '90vh', height: 'calc(100% - 64px)' }
//                 }}
//             >

//                 <DialogTitle
//                     sx={{
//                         bgcolor: theme.palette.primary.main,
//                         color: theme.palette.primary.contrastText,
//                         py: 1.5,
//                         fontSize: '1.1rem',
//                         m: 0,
//                         borderBottom: `1px solid ${theme.palette.divider}`
//                     }}
//                 >

//                  {indexSplitType === "index"
//   ? t("text.splitFiles")
//   : t("text.pendingSplitFiles")}

//                 </DialogTitle>


//                 {/* SEARCH BAR */}

//                 <Box
//                     sx={{
//                         p: 1.5,
//                         borderBottom: `1px solid ${theme.palette.divider}`,
//                         backgroundColor: theme.palette.grey[50],
//                         position: 'sticky',
//                         top: 0,
//                         zIndex: 1101
//                     }}
//                 >

//                     <TextField
//                         fullWidth
//                         size="small"
//                         variant="outlined"
//                         // label="Search File No, File Name, Department"
//                         label={t("text.searchFile")}
//                         value={indexSplitSearch}
//                         onChange={(e) => setIndexSplitSearch(e.target.value)}
//                         sx={{ backgroundColor: 'white' }}
//                     />

//                 </Box>


//                 {/* LOADER */}

//                 {isIndexSplitLoading &&
//                     <LinearProgress
//                         sx={{ position: 'sticky', top: 57, zIndex: 1101 }}
//                     />
//                 }


//                 <DialogContent
//                     dividers
//                     sx={{ p: 0, '&.MuiDialogContent-root': { padding: 0 } }}
//                 >

//                     <TableContainer
//                         component={Paper}
//                         elevation={0}
//                         square
//                         sx={{
//                             maxHeight: 'calc(100vh - 64px - 58px - 53px)'
//                         }}
//                     >

//                         <Table stickyHeader size="small">

//                             <TableHead>

//                                 <TableRow
//                                     sx={{
//                                         '& th': {
//                                             backgroundColor: theme.palette.grey[200],
//                                             fontWeight: 'bold',
//                                             color: theme.palette.text.primary,
//                                             padding: '6px 10px',
//                                             position: 'sticky',
//                                             top: 0,
//                                             zIndex: 1
//                                         }
//                                     }}
//                                 >

//                                     {/* <TableCell>File No</TableCell>
//                                     <TableCell>File Name</TableCell>
//                                     <TableCell>Department</TableCell>
//                                     <TableCell>Status</TableCell> */}
//                                     <TableCell>{t("text.fileNo")}</TableCell>
//                                     <TableCell>{t("text.fileName")}</TableCell>
//                                     <TableCell>{t("text.department")}</TableCell>
//                                     <TableCell>{t("text.status")}</TableCell>

//                                 </TableRow>

//                             </TableHead>


//                             <TableBody>

//                                 {isIndexSplitLoading && !indexSplitData.length ? (

//                                     <TableRow>
//                                         <TableCell colSpan={4} align="center" sx={{ p: 5 }}>
//                                             <CircularProgress />
//                                         </TableCell>
//                                     </TableRow>

//                                 ) : filteredIndexSplitData.length > 0 ? (

//                                     filteredIndexSplitData
//                                         .slice(
//                                             (indexSplitPage - 1) * indexSplitRowsPerPage,
//                                             indexSplitPage * indexSplitRowsPerPage
//                                         )
//                                         .map((row: any, index: number) => (

//                                             <TableRow
//                                                 key={row.docMid}
//                                                 sx={{
//                                                     '&:nth-of-type(odd)': {
//                                                         backgroundColor: theme.palette.action.hover
//                                                     },
//                                                     '&:last-child td': { border: 0 }
//                                                 }}
//                                             >

//                                                 <TableCell sx={{ padding: '6px 10px' }}>
//                                                     {row.fileNo || '-'}
//                                                 </TableCell>

//                                                 <TableCell sx={{ padding: '6px 10px' }}>
//                                                     {row.fileDef || '-'}
//                                                 </TableCell>

//                                                 <TableCell sx={{ padding: '6px 10px' }}>
//                                                     {row.departmentName || '-'}
//                                                 </TableCell>

//                                                 <TableCell sx={{ padding: '6px 10px' }}>

//                                                     <Chip
//                                                         label={row.indexStatus}
//                                                         color={
//                                                             row.indexStatus === "Indexed"
//                                                                 ? "success"
//                                                                 : "warning"
//                                                         }
//                                                     />

//                                                 </TableCell>

//                                             </TableRow>

//                                         ))

//                                 ) : (

//                                     <TableRow>

//                                         <TableCell colSpan={4} align="center">

//                                             <Typography
//                                                 color="textSecondary"
//                                                 sx={{ p: 3, fontStyle: 'italic' }}
//                                             >

//                                                 {indexSplitSearch
//                                                     ? t("text.noRecord")
//                                                     : t("text.noRecord")}

//                                             </Typography>

//                                         </TableCell>

//                                     </TableRow>

//                                 )}

//                             </TableBody>

//                         </Table>

//                     </TableContainer>


//                     {/* PAGINATION */}

//                     <Box
//                         sx={{
//                             display: 'flex',
//                             justifyContent: 'space-between',
//                             alignItems: 'center',
//                             py: 2,
//                             px: 2,
//                             borderTop: `1px solid ${theme.palette.divider}`,
//                             backgroundColor: theme.palette.background.paper
//                         }}
//                     >

//                         <Typography variant="body2" fontWeight="bold">

//                             {t("text.totalRecords")}: {filteredIndexSplitData.length}

//                         </Typography>


//                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

//                             <IconButton
//                                 color="primary"
//                                 disabled={indexSplitPage === 1}
//                                 onClick={() => setIndexSplitPage(p => p - 1)}
//                             >

//                                 <ChevronLeftIcon />

//                             </IconButton>


//                             <Typography variant="body2" sx={{ fontWeight: 600 }}>

//                                 {indexSplitPage} /
//                                 {Math.ceil(filteredIndexSplitData.length / indexSplitRowsPerPage) || 1}

//                             </Typography>


//                             <IconButton
//                                 color="primary"
//                                 disabled={
//                                     indexSplitPage >= Math.ceil(filteredIndexSplitData.length / indexSplitRowsPerPage)
//                                 }
//                                 onClick={() => setIndexSplitPage(p => p + 1)}
//                             >

//                                 <ChevronRightIcon />

//                             </IconButton>

//                         </Box>

//                     </Box>

//                 </DialogContent>


//                 <DialogActions
//                     sx={{
//                         p: 1.5,
//                         borderTop: `1px solid ${theme.palette.divider}`,
//                         backgroundColor: theme.palette.grey[50]
//                     }}
//                 >

//                     <Button
//                         onClick={handleCloseIndexSplitModal}
//                         variant="contained"
//                         color="primary"
//                         size="medium"
//                     >

//                         {t("text.close")}

//                     </Button>

//                 </DialogActions>

//             </Dialog>

//             {/* --- Department Documents Swipeable Drawer (Right Side with Enhanced Table) --- */}
//             <SwipeableDrawer
//                 anchor="right"
//                 open={isDeptDrawerOpen}
//                 onClose={toggleDrawer(false)}
//                 onOpen={toggleDrawer(true)}
//                 disableBackdropTransition={!iOS}
//                 disableDiscovery={iOS}
//                 PaperProps={{
//                     sx: {
//                         width: { xs: '95%', sm: '85%', md: '75%', lg: '65%' }, // Adjusted width
//                         maxWidth: '1100px', // Adjusted max width
//                         height: '100vh',
//                         borderTopLeftRadius: 16,
//                         borderBottomLeftRadius: 16,
//                         borderTopRightRadius: 0,
//                         display: 'flex',
//                         flexDirection: 'column',
//                         boxShadow: theme.shadows[8],
//                         backgroundColor: theme.palette.background.default,
//                     }
//                 }}
//                 sx={{
//                     zIndex: theme.zIndex.drawer + 1 // Ensure it's above other elements
//                 }}
//             >
//                 {/* --- Drawer Header --- */}
//                 <Box sx={{
//                     p: 2,
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'space-between',
//                     borderBottom: `1px solid ${theme.palette.divider}`,
//                     backgroundColor: theme.palette.primary.main,
//                     color: theme.palette.primary.contrastText,
//                     flexShrink: 0, // Prevent header from shrinking
//                 }}>
//                     <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
//                         {selectedDepartment ? `${selectedDepartment}` : 'Department'} Documents
//                     </Typography>
//                     <IconButton
//                         aria-label="close"
//                         onClick={toggleDrawer(false)}
//                         sx={{ color: theme.palette.primary.contrastText }}
//                     >
//                         <CloseIcon />
//                     </IconButton>
//                 </Box>

//                 {/* --- Drawer Search Bar --- */}
//                 <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper, flexShrink: 0 }}>
//                     <TextField
//                         fullWidth size="small" variant="outlined"
//                         // placeholder="Search by File No, PDF Name, Subject..."
//                         placeholder={t("text.searchFile")}
                       
//                         value={deptDataSearchQuery}
//                         onChange={(e) => setDeptDataSearchQuery(e.target.value)}
//                         sx={{
//                             '& .MuiOutlinedInput-root': { borderRadius: '8px' },
//                             backgroundColor: 'white' // Added background color for consistency
//                         }}
//                     />
//                 </Box>

//                 {/* --- Loading Bar --- */}
//                 {isLoadingDeptData && <LinearProgress color="primary" sx={{ width: '100%', flexShrink: 0 }} />}

//                 {/* --- Drawer Content - Scrollable Table --- */}
//                 <Box sx={{ flexGrow: 1, overflow: 'hidden', p: 0, backgroundColor: theme.palette.background.paper, display: 'flex', flexDirection: 'column' }}> {/* Container for table */}
//                     {deptDataError ? (
//                         <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
//                             <Alert severity="warning" sx={{ width: '90%' }}>{deptDataError}</Alert>
//                         </Box>
//                     ) : (
//                         <>
//                             <TableContainer component={Paper} elevation={0} square sx={{ flexGrow: 1, overflowY: 'auto' }}>
//                                 <Table stickyHeader aria-label="collapsible department document table" size="small">
//                                     <TableHead>
//                                         <TableRow sx={{ '& th': { backgroundColor: theme.palette.grey[200], fontWeight: 'bold', color: theme.palette.text.primary, padding: '6px 10px', position: 'sticky', top: 0, zIndex: 1 } }}>
//                                             <TableCell sx={{ width: '50px' }} />
//                                             {/* <TableCell>File No</TableCell>
//                                             <TableCell>File Name</TableCell>
//                                             <TableCell>Department</TableCell>
                                        
//                                             <TableCell>Date</TableCell>
//                                             <TableCell>Files</TableCell>
//                                             <TableCell align="center">Preview</TableCell> */}
//                                             <TableCell>{t("text.fileNo")}</TableCell>
//                                             <TableCell>{t("text.fileName")}</TableCell>
//                                             <TableCell>{t("text.department")}</TableCell>
//                                             <TableCell>{t("text.date")}</TableCell>
//                                             <TableCell>{t("text.files")}</TableCell>
//                                             <TableCell>{t("text.preview")}</TableCell>
//                                         </TableRow>
//                                     </TableHead>
//                                     <TableBody>
//                                         {isLoadingDeptData && !selectedDeptData.length ? (
//                                             <TableRow>
//                                                 <TableCell colSpan={7} align="center" sx={{ p: 5, border: 0 }}> {/* Updated colSpan to 7 */}
//                                                     <CircularProgress />
//                                                     <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>Loading documents...</Typography>
//                                                 </TableCell>
//                                             </TableRow>
//                                         ) : filteredSelectedDeptData.length > 0 ? (
//                                             filteredSelectedDeptData
//                                                 .slice((drawerPage - 1) * drawerRowsPerPage, drawerPage * drawerRowsPerPage)
//                                                 .map((doc, index) => (
//                                                     <DocRow
//                                                         key={doc.docMid}
//                                                         doc={doc}
//                                                         isOpen={openDrawerRowId === doc.docMid}
//                                                         onToggle={() => setOpenDrawerRowId(openDrawerRowId === doc.docMid ? null : doc.docMid)}
//                                                         formatDate={formatDate}
//                                                         theme={theme}
//                                                         isEvenRow={index % 2 === 0}
//                                                     />
//                                                 ))
//                                         ) : (
//                                             <TableRow>
//                                                 <TableCell colSpan={7} align="center" sx={{ p: 4, border: 0 }}> {/* Updated colSpan to 7 */}
//                                                     <Typography color="textSecondary" sx={{ fontStyle: 'italic' }}>
//                                                         {isLoadingDeptData ? t("text.loading") : (deptDataSearchQuery ? t("text.noDocuments") : `No documents found for ${selectedDepartment}.`)}
//                                                     </Typography>
//                                                 </TableCell>
//                                             </TableRow>
//                                         )}
//                                     </TableBody>
//                                 </Table>
//                             </TableContainer>

//                             {/* Pagination for Department Drawer */}
//                             {filteredSelectedDeptData.length > 0 && (
//                                 <Box
//                                     sx={{
//                                         display: "flex",
//                                         justifyContent: "space-between",
//                                         alignItems: "center",
//                                         py: 2,
//                                         px: 2,
//                                         borderTop: `1px solid ${theme.palette.divider}`,
//                                         backgroundColor: theme.palette.background.paper,
//                                         flexShrink: 0
//                                     }}
//                                 >
//                                     <Typography variant="body2" fontWeight="bold">
//                                         {t("text.totalRecords")}: {filteredSelectedDeptData.length}
//                                     </Typography>

//                                     <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
//                                         <IconButton
//                                             color="primary"
//                                             disabled={drawerPage === 1}
//                                             onClick={() => setDrawerPage(prev => prev - 1)}
//                                         >
//                                             <ChevronLeftIcon />
//                                         </IconButton>

//                                         <Typography variant="body2" sx={{ fontWeight: 600 }}>
//                                             {drawerPage} / {Math.ceil(filteredSelectedDeptData.length / drawerRowsPerPage) || 1}
//                                         </Typography>

//                                         <IconButton
//                                             color="primary"
//                                             disabled={drawerPage >= Math.ceil(filteredSelectedDeptData.length / drawerRowsPerPage)}
//                                             onClick={() => setDrawerPage(prev => prev + 1)}
//                                         >
//                                             <ChevronRightIcon />
//                                         </IconButton>
//                                     </Box>
//                                 </Box>
//                             )}
//                         </>
//                     )}
//                 </Box>
//             </SwipeableDrawer>

//         </Box> // End outer Box wrapper
//     );
// }